import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Spin, message, Modal } from 'antd';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { thumbCard, thumbDetail } from '../utils/img';
import dayjs from 'dayjs';
import PageSkeleton, { DetailSkeleton } from '../components/Skeleton';

/* ── palette ── */
const GOLD = '#B8892A';
const BD  = '#DDD7CC';
const BD2 = '#EAE5DC';
const BD3 = '#F2EEE8';
const BG  = '#FAF8F5';
const BG2 = '#F4F1EB';
const BG3 = '#ECE8E0';
const INK = '#1A1410';
const INK2 = '#302418';
const INK3 = '#6B5840';
const INK4 = '#A89070';
const INK5 = '#D4C4A8';
const RED  = '#9B2335';
const GREEN = '#2A6645';
const BLUE = '#1A4F8C';
// 兜底汇率，仅当后端 lot.rate_info 缺失时使用
const FALLBACK_CNY_RATE = 0.043;
// 定期同步家(apre/naniwa 等无单品实时接口,靠后台整场重爬)的重爬周期,须与 worker
// auto-scrape 的 SELF_DISCOVERY_REFRESH_MIN 一致;用于「约每N分钟同步·下次约HH:mm」展示。
const PERIODIC_SYNC_MIN = 60;

// raw_data 里允许展示的英文内容字段（其余纯英文 key 视为内部字段）+ 友好标签
const SPEC_CONTENT_LABELS = { item_information: '商品信息', remarks: '备注', note: '备注' };
// 判定 raw_data 字段是否为内部/技术字段（不对用户展示）
// 规则：拍卖会真实规格标签均为日文/中文；纯英文 key（is_*/_flg/web_auction_type 等）基本是内部 API 字段
function isInternalSpecKey(k, v) {
  if (Object.prototype.hasOwnProperty.call(SPEC_CONTENT_LABELS, k)) return false;
  if (typeof v === 'boolean' || v === 'true' || v === 'false') return true;
  return /^[\x20-\x7E]+$/.test(String(k)); // 纯 ASCII（英文）键
}

const LOT_STATUS_MAP = {
  published: { label: '在售', color: BLUE, bg: 'rgba(26,79,140,0.1)' },
  bidding: { label: 'LIVE', color: RED, bg: 'rgba(155,35,53,0.07)' },
  live: { label: 'LIVE', color: RED, bg: 'rgba(155,35,53,0.07)' },
  preview: { label: '预告', color: GOLD, bg: 'rgba(184,137,42,0.10)' },
  ended: { label: '已结束', color: INK4, bg: BG3 },
  closed: { label: '已结束', color: INK4, bg: BG3 },
};

// 委托状态中文(代拍模型:委托建好后要等结拍前才真正在源站入札)
const MANDATE_STATUS_LABEL = {
  pending_review: '审核中',
  approved: '已委托 · 待结拍前入札',
  queued: '排队中',
  executing: '入札中',
  won: '🏆 已中标',
};

/* ① 成色等级 — 从 LOT.rank 字段动态设置 */
const RANK_COLORS = {
  S: { bg: BLUE, color: '#fff', label: 'S ランク' },
  A: { bg: GREEN, color: '#fff', label: 'A ランク' },
  B: { bg: '#3D68A8', color: '#fff', label: 'B ランク' },
  C: { bg: '#8C5A0A', color: '#fff', label: 'C ランク' },
  D: { bg: RED, color: '#fff', label: 'D ランク' },
};

function formatJPY(val) {
  if (val == null) return '\u2014';
  return '\u00a5' + Number(val).toLocaleString('ja-JP');
}

/* ② 倒计时精确到秒，剩余3小时变红闪烁 */
function useCountdown(endAt) {
  const [state, setState] = useState({ text: '', urgent: false });
  useEffect(() => {
    if (!endAt) { setState({ text: '', urgent: false }); return; }
    const update = () => {
      const diff = dayjs(endAt).diff(dayjs(), 'second');
      if (diff <= 0) { setState({ text: '已結束', urgent: false }); return; }
      const d = Math.floor(diff / 86400), h = Math.floor((diff % 86400) / 3600),
        m = Math.floor((diff % 3600) / 60), s = diff % 60;
      setState({
        text: `${d}日 ${String(h).padStart(2,'0')}時間 ${String(m).padStart(2,'0')}分 ${String(s).padStart(2,'0')}秒`,
        urgent: diff < 10800, // 3 hours = 10800s
      });
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [endAt]);
  return state;
}

/* ③ 快捷加价 — 根据当前价格档位动态生成 */
function getQuickBidSteps(price) {
  if (price < 50000) {
    return [500, 1000, 5000, 10000, 30000, 50000];
  } else if (price < 1000000) {
    return [5000, 10000, 50000, 100000, 300000, 500000];
  } else {
    return [10000, 50000, 100000, 500000, 1000000, 5000000];
  }
}

function fmtStep(v) {
  if (v >= 10000) return `${v / 10000}万`;
  return v.toLocaleString();
}

/* ── CSS keyframes injected once ── */
const styleId = 'lot-detail-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes ld-blink{0%,100%{opacity:1}50%{opacity:.5}}
    @keyframes ld-flash{0%{background:rgba(42,102,69,.22)}100%{background:transparent}}
    @keyframes ld-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
  `;
  document.head.appendChild(style);
}

/* ══ LightboxViewer — 图片放大灯箱 ══
   Desktop: 暗色遮罩 + 图片 + 跟随鼠标的放大镜 (2.5x)
   Mobile:  全屏遮罩 + 原生捏合缩放 + 左右滑动翻页
*/
function LightboxViewer({ images, index, onClose, onChange, isMobile }) {
  const [mousePos, setMousePos] = useState(null);
  const [imgSize, setImgSize]   = useState({ w: 0, h: 0 });
  const imgRef       = useRef(null);
  const touchStartX  = useRef(null);

  const LENS = 180;   // lens diameter (px)
  const ZOOM = 2.8;   // magnification factor

  const total  = images.length;
  const cur    = images[Math.min(index, total - 1)];
  const goPrev = (e) => { e?.stopPropagation(); onChange(i => Math.max(0, i - 1)); };
  const goNext = (e) => { e?.stopPropagation(); onChange(i => Math.min(total - 1, i + 1)); };

  /* Measure rendered image size (needed for background-size of lens) */
  const measureImg = () => {
    if (imgRef.current) {
      const r = imgRef.current.getBoundingClientRect();
      setImgSize({ w: r.width, h: r.height });
    }
  };
  useEffect(() => {
    measureImg();
    window.addEventListener('resize', measureImg);
    return () => window.removeEventListener('resize', measureImg);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  /* Desktop: track mouse over image */
  const handleMouseMove = (e) => {
    if (!imgRef.current) return;
    const r = imgRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (x >= 0 && y >= 0 && x <= r.width && y <= r.height) {
      setMousePos({ x, y });
      setImgSize({ w: r.width, h: r.height });
    } else {
      setMousePos(null);
    }
  };

  /* Mobile: swipe left/right to navigate */
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) { if (dx < 0) goNext(); else goPrev(); }
    touchStartX.current = null;
  };

  /* Lens position (centered on cursor) */
  const lensLeft = mousePos ? mousePos.x - LENS / 2 : 0;
  const lensTop  = mousePos ? mousePos.y - LENS / 2 : 0;
  const bgX      = mousePos ? -(mousePos.x * ZOOM - LENS / 2) : 0;
  const bgY      = mousePos ? -(mousePos.y * ZOOM - LENS / 2) : 0;

  /* Nav button shared style */
  const navBtn = (extra) => ({
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 10001,
    width: 44, height: 44, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 26,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(6px)', transition: 'background .15s',
    ...extra,
  });

  return (
    /* Backdrop — click outside image to close */
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.94)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        userSelect: 'none',
      }}
    >
      {/* Close × */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 14, right: 14, zIndex: 10002,
          width: 38, height: 38, borderRadius: '50%', border: 'none',
          background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 18,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(6px)',
        }}
      >✕</button>

      {/* ← Prev */}
      {total > 1 && index > 0 && (
        <button onClick={goPrev} style={navBtn({ left: 14 })}>‹</button>
      )}

      {/* → Next */}
      {total > 1 && index < total - 1 && (
        <button onClick={goNext} style={navBtn({ right: 14 })}>›</button>
      )}

      {/* ─ Image + magnifier container ─ */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseMove={!isMobile ? handleMouseMove : undefined}
        onMouseLeave={!isMobile ? () => setMousePos(null) : undefined}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
        style={{
          position: 'relative',
          maxWidth:  isMobile ? '100vw' : '82vw',
          maxHeight: isMobile ? '78vh' : '82vh',
          cursor: !isMobile ? (mousePos ? 'crosshair' : 'zoom-in') : 'default',
          touchAction: isMobile ? 'pinch-zoom' : 'none',
          overflow: isMobile ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <img
          ref={imgRef}
          src={cur?.url}
          alt=""
          onLoad={measureImg}
          draggable={false}
          style={{
            display: 'block',
            maxWidth:  isMobile ? '100vw' : '82vw',
            maxHeight: isMobile ? '78vh' : '82vh',
            objectFit: 'contain',
            userSelect: 'none',
          }}
        />

        {/* Magnifier lens — desktop only, while mouse is over image */}
        {!isMobile && mousePos && imgSize.w > 0 && (
          <div style={{
            position: 'absolute',
            left: lensLeft,
            top:  lensTop,
            width:  LENS,
            height: LENS,
            borderRadius: '50%',
            border: '2.5px solid rgba(255,255,255,0.85)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
            backgroundImage: `url(${cur?.url})`,
            backgroundSize:     `${imgSize.w * ZOOM}px ${imgSize.h * ZOOM}px`,
            backgroundPosition: `${bgX}px ${bgY}px`,
            backgroundRepeat: 'no-repeat',
            pointerEvents: 'none',
            overflow: 'hidden',
          }} />
        )}
      </div>

      {/* ─ Bottom: counter + thumbnails ─ */}
      {total > 1 && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          }}
        >
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontFamily: 'var(--mono,monospace)' }}>
            {index + 1} / {total}
          </div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'center', maxWidth: '90vw' }}>
            {images.map((img, i) => (
              <div
                key={i}
                onClick={(e) => { e.stopPropagation(); onChange(i); }}
                style={{
                  width: 44, height: 44, borderRadius: 5, overflow: 'hidden',
                  border: `2px solid ${i === index ? '#fff' : 'rgba(255,255,255,0.18)'}`,
                  cursor: 'pointer', flexShrink: 0, transition: 'border-color .15s',
                }}
              >
                <img src={thumbCard(img.url)} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mobile swipe hint */}
      {isMobile && total > 1 && (
        <div style={{
          position: 'absolute',
          bottom: total > 1 ? 80 : 20,
          left: '50%', transform: 'translateX(-50%)',
          fontSize: 11, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}>
          左右滑动切换 · 双指放大
        </div>
      )}
    </div>
  );
}

export function LotDetailContent({ lotId: propLotId, isDialog = false, onBidPlaced }) {
  const params = useParams();
  const id = propLotId || params.id;
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lot, setLot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);
  const [maxBid, setMaxBid] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [bidOutcome, setBidOutcome] = useState(null); // { autoReview, bidSuccess, isLeading, msg }
  const [bidError, setBidError] = useState(null); // 就地失败提示 { msg, minBid } —— 出价失败不跳走，当场显示+预填最低价重出
  const [priceFlash, setPriceFlash] = useState(false);
  // 价格同步状态：at=最近一次成功取到价格的时刻(客户端时间)，ok=最近一次手动刷新是否成功。
  // 用于在现价旁展示「几点几分刷新(北京时间)」，以及手动刷新失败时提示失败+上次更新时间。
  const [syncInfo, setSyncInfo] = useState(null);
  const fmtBJ = (d) => d ? new Date(d).toLocaleTimeString('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit', hour12: false }) : '';
  const [submitStrategy, setSubmitStrategy] = useState('immediate');
  const [submitBeforeMins, setSubmitBeforeMins] = useState(5);
  const [infoTab, setInfoTab] = useState('overview');
  // 出价表单默认收起,点「立即出价」按钮才展开(仿千旗中古,详情页更干净)。
  const [bidPanelOpen, setBidPanelOpen] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [relatedLots, setRelatedLots] = useState([]);
  const [bidHistory, setBidHistory] = useState([]);
  const [bidHistoryLoading, setBidHistoryLoading] = useState(false);
  const [translatedDesc, setTranslatedDesc] = useState('');
  const [translating, setTranslating] = useState(false);
  const [translatedTitle, setTranslatedTitle] = useState('');
  const [translatingTitle, setTranslatingTitle] = useState(false);
  // 中日翻译开关：标题当前显示语言。初值在 lot 加载后按 title_zh 是否存在决定
  const [titleLang, setTitleLang] = useState('ja');
  const [translatedSpecs, setTranslatedSpecs] = useState({});
  const [translatingSpecs, setTranslatingSpecs] = useState(false);
  // 竞拍面板用「实时可用余额」，不能用登录时写入的陈旧 user.balance（充值后不刷新会显示为旧值/0）
  const [liveBalance, setLiveBalance] = useState(null);
  // 单件无结束时间(如大吉:只有整场 end_at)时回退到场次结束时间,避免「残り時間 —」空白。
  const { text: countdown, urgent } = useCountdown(lot?.end_at || lot?.session_end);

  /* ── 工具弹窗状态 ── */
  const [showCostCalc, setShowCostCalc] = useState(false);
  const [showMetalCalc, setShowMetalCalc] = useState(false);
  const [showBidRules, setShowBidRules] = useState(false);
  // 金属计价器输入
  const [metalType, setMetalType] = useState('gold');
  const [metalWeight, setMetalWeight] = useState('');
  const [metalPurity, setMetalPurity] = useState('');
  const [metalDiscount, setMetalDiscount] = useState('1');
  // 成本计算器自定义出价
  const [costBidInput, setCostBidInput] = useState('');

  /* ⑥ 备注 — localStorage 持久化 */
  const memoKey = `lot_memo_${id}`;
  const [memo, setMemo] = useState(() => {
    try { return localStorage.getItem(memoKey) || ''; } catch { return ''; }
  });
  const saveMemo = () => {
    try { localStorage.setItem(memoKey, memo); } catch {}
    message.success('备注已保存');
  };

  const fetchBidHistory = async () => {
    if (!isLoggedIn) return;
    setBidHistoryLoading(true);
    try {
      const { data } = await api.get(`/lots/${id}/my-bids`);
      setBidHistory(data.data || []);
    } catch { /* silent */ }
    finally { setBidHistoryLoading(false); }
  };

  // 实时拉取可用余额（与后端冻结保证金时校验的 available_cny 一致），修复充值后竞拍页仍显示旧余额/0
  const fetchBalance = async () => {
    if (!isLoggedIn) return;
    try {
      const { data } = await api.get('/deposits/balance');
      if (data && data.available_cny != null) setLiveBalance(Number(data.available_cny));
    } catch { /* silent — 回退 user.balance */ }
  };
  useEffect(() => {
    fetchBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  /* 中日翻译开关 — 切换标题显示语言，切到中文时懒翻译并缓存 */
  const toggleTitleLang = async () => {
    if (translatingTitle) return;
    // 目标语言：当前中文则切回日文；否则切到中文
    if (titleLang === 'zh') { setTitleLang('ja'); return; }
    const zh = translatedTitle || lot?.title_zh;
    if (zh) { setTitleLang('zh'); return; }
    // 没有中文标题 —— 调用懒翻译接口(会永久缓存到 title_zh)
    if (!lot?.title_ja) { setTitleLang('zh'); return; }
    setTranslatingTitle(true);
    try {
      const res = await api.post(`/lots/${lot.id}/translate`);
      setTranslatedTitle(res.data?.title_zh || '');
      setTitleLang('zh');
    } catch { message.error('翻译服务暂不可用'); }
    finally { setTranslatingTitle(false); }
  };

  useEffect(() => {
    setLoading(true);
    setSelectedImg(0);
    setBidHistory([]);

    api.get(`/lots/${id}`)
      .then(({ data }) => {
        setLot(data);
        setSyncInfo({ at: new Date(), ok: true }); // 页面首次载入即为一次「取到最新缓存价」
        // 从后端 lot 数据初始化收藏状态
        setWishlisted(!!data.is_watchlisted);
        // 中日翻译开关初值：有中文标题默认显示中文，否则显示日文
        setTranslatedTitle('');
        setTitleLang(data.title_zh ? 'zh' : 'ja');
        /* 市场参考价（AI估价）已按需求下线，暂停拉取 */
        /* ⑦ 同场次推荐 — 加载5件 */
        if (data.session_id) {
          api.get(`/lots?session_id=${data.session_id}&limit=6`).then(r => {
            setRelatedLots((r.data?.data || []).filter(l => String(l.id) !== String(id)).slice(0, 5));
          }).catch(() => {});
        }
      })
      .catch(() => message.error('获取拍品详情失败'))
      .finally(() => setLoading(false));
    fetchBidHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Popup 打开时静默同步一次最新价格（含结拍后的 lot；用户没主动点，不弹 message）
  const autoRefreshedForId = useRef(null);
  useEffect(() => {
    if (!lot || !id) return;
    if (autoRefreshedForId.current === id) return;
    autoRefreshedForId.current = id;
    api.post(`/lots/${id}/sync`, null, { timeout: 45000 })
      .then(({ data }) => { setLot(data); setSyncInfo({ at: new Date(), ok: true }); })
      .catch(() => { /* 静默失败 — 用户没主动操作;保留载入时的 syncInfo */ });
  }, [lot, id]);

  // Auto-poll: 每60秒自动同步最新价格，tab不可见时暂停，拍品结束后停止
  useEffect(() => {
    if (!lot || !id) return;
    const isEnded = lot.status === 'ended' || lot.status === 'closed' ||
      (lot.end_at && dayjs(lot.end_at).isBefore(dayjs()));
    if (isEnded) return;

    let timer = null;
    const poll = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const { data } = await api.post(`/lots/${id}/sync`, null, { timeout: 45000 });
        setSyncInfo({ at: new Date(), ok: true });
        setLot(prev => {
          if (!prev) return data;
          const oldPrice = Number(prev.current_price_jpy || 0);
          const newPrice = Number(data.current_price_jpy || 0);
          if (newPrice > oldPrice) {
            setPriceFlash(true);
            setTimeout(() => setPriceFlash(false), 1500);
          }
          return data;
        });
      } catch { /* silent — don't disturb user */ }
    };

    timer = setInterval(poll, 60_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lot?.status, lot?.end_at]);

  const handleBidSubmit = async () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    // 参拍前必须绑定手机号（微信快捷登录用户默认无手机，无法识别身份）
    if (!(user?.phone && String(user.phone).trim())) {
      navigate('/complete-profile?redirect=' + encodeURIComponent('/lots/' + id));
      return;
    }
    const bidAmount = Number(maxBid);
    if (!maxBid || bidAmount <= 0) { message.error('请输入有效的出价金额'); return; }
    // 単位チェック
    const unit = getBidUnit(bidAmount);
    if (bidAmount % unit !== 0) {
      message.error(`出价金额必须是 ¥${unit.toLocaleString('ja-JP')} 的整数倍（例：${Math.floor(bidAmount / unit) * unit}、${Math.ceil(bidAmount / unit) * unit}）`);
      return;
    }
    // 現在価格チェック
    const curPrice = Number(lot?.current_price_jpy || lot?.start_price_jpy || 0);
    if (bidAmount <= curPrice) {
      message.error(`出价必须高于当前价格 ${formatJPY(curPrice)}`);
      return;
    }

    // ── 二次确认(防误点):清楚展示最终出价金额 + 人民币约值,确认后才真正提交 ──
    const _mm = lot?.my_mandate;
    const _isAdjust = _mm && ['pending_review', 'approved', 'queued', 'executing', 'bid_failed'].includes(_mm.status);
    Modal.confirm({
      title: _isAdjust ? '确认调整出价上限？' : '确认提交委托竞拍？',
      okText: _isAdjust ? '确认调整' : '确认出价',
      cancelText: '再想想',
      centered: true,
      content: (
        <div style={{ fontSize: 14, lineHeight: 1.9 }}>
          <div style={{ color: '#6B5E4E' }}>{lot?.title_zh || lot?.title_ja || ''}</div>
          <div style={{ marginTop: 6 }}>
            {_isAdjust ? '新的出价上限' : '您的最高出价'}：
            <b style={{ fontSize: 20, color: '#B8892A', fontFamily: 'var(--mono,monospace)' }}> ¥{bidAmount.toLocaleString('ja-JP')}</b>
            <span style={{ color: '#A8998A', fontSize: 13 }}>（≈{Math.round(bidAmount * CNY_RATE).toLocaleString()}元）</span>
          </div>
          {lot?.bid_type !== 'sealed' && (
            <div style={{ color: '#A8998A', fontSize: 12 }}>当前价 ¥{curPrice.toLocaleString('ja-JP')}</div>
          )}
          <div style={{ marginTop: 8, fontSize: 12, color: '#8C5A0A' }}>
            系统将在不超过此金额内替您竞拍，超出则自动止步。
          </div>
        </div>
      ),
      onOk: () => executeBid(),
    });
  };

  // 二次确认通过后真正执行出价/加价
  const executeBid = async () => {
    setSubmitting(true);
    setBidError(null); // 每次提交前清掉上一次的失败提示
    try {
      // 已有活跃委托 → 走「调整出价上限」(加价)，而非新建委托(后端会 409「请去竞拍中心调整」)。
      // 一件一委托：同一拍品只维护一个委托，改价=调整这个委托，不再跳去竞拍中心。
      const mm = lot?.my_mandate;
      const activeMandate = mm && ['pending_review', 'approved', 'queued', 'executing', 'bid_failed'].includes(mm.status) ? mm : null;
      if (activeMandate) {
        const { data: adj } = await api.patch(`/mandates/${activeMandate.id}/adjust-bid`, { max_bid_jpy: Number(maxBid) });
        onBidPlaced?.(id);
        // 刷新 lot（更新 my_mandate 授权上限 + 当前价）
        try { const r = await api.post(`/lots/${id}/sync`); setLot(r.data); }
        catch { try { const r = await api.get(`/lots/${id}`); setLot(r.data); } catch {} }
        setPriceFlash(true); setTimeout(() => setPriceFlash(false), 1200);
        message.success(`已调整出价上限至 ${formatJPY(Number(maxBid))}${adj?.rearmed ? '，将重新出价' : ''}`);
        fetchBidHistory();
        return; // finally 仍会跑（刷新余额 + 解锁按钮）
      }
      const { data } = await api.post('/mandates', {
        lot_id: id, max_bid_jpy: Number(maxBid), bid_timing_secs: 600, notes: memo,
        submit_strategy: submitStrategy,
        submit_before_mins: submitStrategy === 'immediate' ? 5 : submitBeforeMins,
      });

      // Notify parent (Browse page) to immediately show 竞拍中 badge
      onBidPlaced?.(id);

      if (data.auto_review) {
        if (data.bid_triggered) {
          if (data.bid_success) {
            // 入札成功 → sync で最新価格を取得し、入札額と比較して領先判定
            let isLeadingAfterSync = data.is_leading;
            try {
              const r = await api.post(`/lots/${id}/sync`);
              setLot(r.data);
              const syncedPrice = r.data.current_price_jpy;
              if (syncedPrice > 0) {
                isLeadingAfterSync = syncedPrice <= Number(maxBid);
              }
            } catch {
              try { const r = await api.get(`/lots/${id}`); setLot(r.data); } catch {}
            }
            setBidOutcome({ autoReview: true, bidSuccess: true, isLeading: isLeadingAfterSync, msg: data.customer_message });
            setSubmitted(true);
            // 价格区域闪烁提示已刷新
            setPriceFlash(true);
            setTimeout(() => setPriceFlash(false), 1200);
            // 刷新入札记录
            fetchBidHistory();
          } else {
            // 入札失败 → 不跳走、不锁表单：当场显示失败横幅 + 预填精确最低价，用户直接再点「立即委托竞拍」重出
            const rawMsg = String(data.customer_message || '入札失败，请稍后重试');
            const minMt = /\|MIN_BID:(\d+)/.exec(rawMsg);
            const minBid = minMt ? Number(minMt[1]) : null;
            setBidError({ msg: rawMsg.replace(/\s*\|MIN_BID:\d+/g, '').trim(), minBid, code: data.error_code });
            if (minBid) setMaxBid(String(minBid)); // 自动填入最低可出价，省得用户来回猜
          }
        } else {
          // 不支持自动入札的拍卖行
          setBidOutcome({ autoReview: true, bidSuccess: null, msg: data.customer_message });
          setSubmitted(true);
          message.success({ content: data.customer_message || '委托已提交，等待处理', duration: 6 });
        }
      } else {
        // 人工审核模式
        setBidOutcome({ autoReview: false });
        setSubmitted(true);
        message.success('委托提交成功！保证金已冻结，等待审核');
        fetchBidHistory();
      }
    } catch (err) {
      // 后端拦截：未绑定手机号 → 跳完善资料页（前端提前拦一道，这里是兜底）
      if (err.response?.data?.code === 'PHONE_REQUIRED') {
        message.warning('参拍前请先绑定手机号');
        navigate('/complete-profile?redirect=' + encodeURIComponent('/lots/' + id));
        return;
      }
      const d = err.response?.data || {};
      const errMsg = d.error || d.message || err.message || '提交委托失败';
      // 后端就地拦截（出价 < 当前价+加价一档）会返回 min_bid_jpy：当场显示 + 预填最低价重出，不用弹窗跳走
      if (d.min_bid_jpy) {
        setBidError({ msg: errMsg, minBid: Number(d.min_bid_jpy) });
        setMaxBid(String(Number(d.min_bid_jpy)));
      } else {
        console.error('[mandate submit]', err.response?.status, err.response?.data, err.message);
        message.error(errMsg);
      }
    } finally {
      fetchBalance(); // 提交后刷新可用余额（冻结保证金后会下降）
      setSubmitting(false);
    }
  };

  /* 加価単位を返す: 拍卖行に bid_steps が設定されていればそれを使用、なければデフォルト。
     用函数声明(hoisted)而非 const 箭头 —— 它在声明前的 handleBidSubmit 里被调用,
     const 形式会被 terser 压缩成 "Cannot access before initialization" 的 TDZ 白屏。 */
  function getBidUnit(price) {
    const p = Number(price) || 0;
    // Use per-house bid_steps from lot.house_bid_steps
    let steps = null;
    try {
      const raw = lot?.house_bid_steps;
      steps = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null;
    } catch {}
    if (steps && steps.length > 0) {
      // Steps are sorted ascending by below (null = infinity at end)
      for (const s of steps) {
        if (s.below === null || s.below === undefined || p < s.below) return Number(s.step) || 1000;
      }
      return Number(steps[steps.length - 1].step) || 1000;
    }
    // Fallback: 10,000未満→500、以上→1,000
    if (p < 10000) return 500;
    return 1000;
  }

  /* ③ 点击在当前输入值基础上累加（自动对齐单位）*/
  const addQuickBid = (step) => {
    const cur = Number(maxBid) || Number(lot?.current_price_jpy || lot?.start_price_jpy || 0);
    const next = cur + step;
    const unit = getBidUnit(next);
    setMaxBid(String(Math.ceil(next / unit) * unit));
  };

  /* 更新按钮 — 触发单品实时同步后刷新显示 */
  const refreshLot = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      // POST /api/lots/:id/sync — 先从拍卖网站取最新价格写入 DB，再返回最新数据。
      // ⚠️ eco/rs/myauction 等走 JP 代理的家取价要 3-30s(偶发 ECONNRESET 重试),8s 超时太短 →
      //    后端其实在跑/能成功,但前端 8s 就抛「同步失败」误报。放宽到 45s 覆盖慢家。
      const { data } = await api.post(`/lots/${id}/sync`, null, { timeout: 45000 });
      setLot(data);
      setSyncInfo({ at: new Date(), ok: true }); // 成功取到实时价 → 记录本次刷新时刻
      const sync = data._sync;
      if (sync?.updated) {
        if (sync.status === 'closed') {
          message.warning('该拍品已结束竞拍');
        } else {
          const bidCountStr = sync.bidCount != null ? `  /  ${sync.bidCount} 次入札` : '';
          message.success(`已更新：¥${Number(sync.currentPrice).toLocaleString('ja-JP')}${bidCountStr}`);
        }
      } else if (sync?.rateLimited) {
        // NJ API レート制限中 — キャッシュデータを表示（警告不要）
        message.info('NJ行情由定期爬虫自动更新，当前显示最新缓存数据');
      } else if (sync?.unsupported) {
        // 该拍卖行无单品实时同步：暗拍场结拍前本就不公开实时价；明拍家由后台定期整场重爬更新。非错误。
        if (data.bid_type === 'sealed') {
          message.info('暗拍场结拍前不公开实时价，数据已是最新');
        } else {
          // 定期同步家:给出真实周期 + 上次同步(=lot.updated_at 后台整场重爬时刻) + 下次约同步时间。
          const lastT = data.updated_at ? dayjs(data.updated_at) : null;
          const detail = lastT
            ? ` · 上次 ${lastT.format('MM-DD HH:mm')} · 下次约 ${lastT.add(PERIODIC_SYNC_MIN, 'minute').format('HH:mm')}（北京时间）`
            : '';
          message.info({ content: `该拍卖行约每 ${PERIODIC_SYNC_MIN} 分钟整场同步一次价格，数据已是最新${detail}`, duration: 7 });
        }
      } else if (sync?.note === 'not_found') {
        // 源站已下架/无该单品实时页(如 timeless 下架、otakaraya JS 渲染场)→ 显示缓存价
        message.info('该商品暂无法获取实时价（可能已下架或结拍），显示缓存价');
      } else if (sync?.note === 'scan_timeout' || sync?.note === 'not_in_list') {
        // 该行无单品实时接口(需扫全列表)，取实时价超时/未命中 → 显示缓存价，约每小时自动更新。
        message.warning('实时价获取超时，显示缓存价（该行约每小时自动更新）');
      } else if (sync?.error) {
        // 同步失败但仍返回了 DB 数据（降级模式）
        message.warning(`同步失败，显示数据库缓存：${sync.error}`);
      } else {
        message.info('数据已是最新');
      }
    } catch (err) {
      // 完全失败时退回只读 DB。保留上次成功时间(at)，仅把 ok 标为 false → 现价旁显示「刷新失败·上次 HH:mm」。
      try {
        const { data } = await api.get(`/lots/${id}`);
        setLot(data);
      } catch {}
      setSyncInfo(prev => ({ at: prev?.at || null, ok: false }));
      message.error(err.response?.data?.error || '刷新失败，请稍后再试（现价为上次同步结果）');
    } finally {
      setRefreshing(false);
    }
  };

  /* Lightbox keyboard handler — dep array doesn't reference mediaItems (defined later) */
  useEffect(() => {
    const handler = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') setSelectedImg(i => (i > 0 ? i - 1 : 0));
      if (e.key === 'ArrowRight') setSelectedImg(i => i + 1); // prevImg/nextImg bound-check at render
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen]);

  /* ⑥ 备注删除 */
  const deleteMemo = () => {
    setMemo('');
    try { localStorage.removeItem(memoKey); } catch {}
    message.success('备注已删除');
  };

  if (loading) return <DetailSkeleton />;
  if (!lot) return <div style={{ padding: 40, textAlign: 'center', color: INK4 }}>拍品不存在或已下架</div>;

  let images = [];
  try { images = typeof lot.image_urls === 'string' ? JSON.parse(lot.image_urls) : (lot.image_urls || []); } catch {}

  let videos = [];
  try { videos = typeof lot.video_urls === 'string' ? JSON.parse(lot.video_urls) : (lot.video_urls || []); } catch {}

  // 画像 + 動画を統合したメディア配列
  const mediaItems = [
    ...images.map(url => ({ type: 'image', url })),
    ...videos.map(url  => ({ type: 'video', url })),
  ];

  /* メディア左右切換（画像 + 動画）— 定义在 mediaItems 之后,避免声明前引用 */
  const prevImg = () => setSelectedImg(i => (i > 0 ? i - 1 : mediaItems.length - 1));
  const nextImg = () => setSelectedImg(i => (i < mediaItems.length - 1 ? i + 1 : 0));

  let itemInfo = {};
  try {
    const _parsed = typeof lot.raw_data === 'string' ? JSON.parse(lot.raw_data) : lot.raw_data;
    // JSON.parse('null')/数字/数组 都不抛错但不是对象 → 后面 itemInfo['x'] / Object.entries 会崩。
    // 统一收敛成普通对象,防止稀疏/异形 raw_data 拍品(搜索命中)点进来白屏。
    itemInfo = (_parsed && typeof _parsed === 'object' && !Array.isArray(_parsed)) ? _parsed : {};
    // JWA(auctionchannel)等的 raw_data 键是「日文 / 中文」格式(如 "素材 / 材质":"Pt900"、
    // "ブランド / 品牌":"ノンブランド"、"カラー / 颜色")。下方规格行按纯日文键(素材/ブランド/カラー)
    // 查找 → 对不上就整片空白(商品参数看不到)。这里给每个「X / Y」键补一个纯日文别名 X,让现有查找命中。
    for (const [k, v] of Object.entries(itemInfo)) {
      const m = typeof k === 'string' && k.match(/^(.+?)\s*\/\s*.+$/);
      if (m && m[1] && !(m[1] in itemInfo)) itemInfo[m[1]] = v;
    }
  } catch { itemInfo = {}; }

  const status = LOT_STATUS_MAP[lot.status] || { label: lot.status, color: INK4, bg: BG3 };
  // 2026-07-04: 时间感知(与 453-454 的 price-sync 判定一致)。之前只看 status,
  // 当 end_at 已过但爬虫尚未把 status 回写 closed 时(finalize 有延迟/页面状态陈旧),
  // 出价面板照常显示 → 点"立即委托竞拍"被后端 mandates 拒"拍品不存在或已结束"。
  // `lot.end_at &&` 短路守卫必须保留:end_at 为 null 时不调 dayjs,退回纯 status 判定
  // (daikichi 有 45 件 published 拍品 end_at=NULL,不能误判成已结束)。
  const isEnded = lot.status === 'ended' || lot.status === 'closed' ||
    (lot.end_at && dayjs(lot.end_at).isBefore(dayjs()));
  const curPrice = lot.current_price || lot.current_price_jpy || lot.start_price || lot.start_price_jpy || 0;
  const bidVal = Number(maxBid) || 0;

  /* ① 成色等级 — 从 rank / raw_data.商品ランク 动态获取 */
  const rawRank = String(lot.rank || lot.condition || itemInfo['商品ランク'] || '').replace(/商品ランクについて/g, '').trim();
  const rankKey = rawRank.charAt(0).toUpperCase();
  const rankInfo = RANK_COLORS[rankKey] || null;

  /* ③ 动态加价档位 */
  const quickSteps = getQuickBidSteps(curPrice);

  /* ④ 费用实时预算 */
  const TAX_RATE           = 0.10;   // 消費税（日本 10%，固定）
  // 手续费率：从用户等级获取，未登录/无等级数据时默认 3%
  const SERVICE_FEE_RATE   = Number(user?.service_fee_rate ?? 0.03);
  // 保证金冻结比例：从会员设定获取（deposit_required=0 时不需要保证金）
  const depositRequired    = user?.deposit_required !== undefined ? Number(user.deposit_required) : 1;
  const DEPOSIT_FREEZE_RATE = depositRequired ? Number(user?.deposit_rate ?? 0.10) : 0;
  // 汇率：结拍 lot 用结拍时锁定的 BOC 历史值；进行中 lot 用当前 BOC 实时值（后端 /lots/:id 已返回 rate_info）
  const rateInfo = lot.rate_info;
  const CNY_RATE = rateInfo?.rate ? Number(rateInfo.rate) : FALLBACK_CNY_RATE;
  const rateLocked = !!rateInfo?.locked;
  const taxJpy    = bidVal ? Math.round(bidVal * TAX_RATE) : 0;
  const svcFeeJpy = bidVal ? Math.round(bidVal * SERVICE_FEE_RATE) : 0;
  const svcFeeCny = bidVal ? Math.round(bidVal * CNY_RATE * SERVICE_FEE_RATE) : 0;
  const freezeCny = bidVal && depositRequired ? Math.ceil(bidVal * CNY_RATE * DEPOSIT_FREEZE_RATE) : 0;
  const userBalance = liveBalance != null ? liveBalance : Number(user?.balance ?? user?.available_cny ?? 0);
  const balanceInsufficient = bidVal > 0 && freezeCny > userBalance;
  const phoneMissing = isLoggedIn && !(user?.phone && String(user.phone).trim()); // 未绑手机 → 引导先补全
  // KYC 不再强制要求，仅需邮箱认证 + 保证金即可参拍（必须在 bidBtnInactive 之前声明，否则压缩后触发 TDZ 白屏）
  const kycVerified = true;
  // 未绑手机时按钮保持可点(点了跳绑定页),故此时不因余额不足而置灰
  const bidBtnInactive = isEnded || (!phoneMissing && balanceInsufficient) || !kycVerified;

  return (
    <>
    <div style={{ background: BG, ...(isDialog ? {} : { minHeight: 'calc(100vh - 120px)' }) }}>
      {/* ── Breadcrumb — hidden on mobile ── */}
      {!isMobile && (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '10px 32px 0', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: INK4, fontFamily: 'var(--mono,monospace)' }}>
          <Link to="/" style={{ color: INK4, textDecoration: 'none' }}>首页</Link>
          <span style={{ color: INK5 }}>›</span>
          <Link to="/auction-hub" style={{ color: INK4, textDecoration: 'none' }}>拍品浏览</Link>
          <span style={{ color: INK5 }}>›</span>
          {lot.session_name && (<><span style={{ color: INK4 }}>{lot.session_name}</span><span style={{ color: INK5 }}>›</span></>)}
          <span style={{ color: INK }}>{lot.box_no && lot.item_no ? `${lot.box_no}-${String(lot.item_no).padStart(2,'0')}` : (lot.external_lot_id || lot.box_no || lot.item_no || lot.lot_number || '')}</span>
        </div>
      )}

      {/* ── Main layout ── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px 12px 80px' : '16px 32px 100px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1fr)', gap: isMobile ? 16 : 32, alignItems: 'start', marginBottom: isMobile ? 20 : 40 }}>

          {/* ══ LEFT: Image gallery (sticky on desktop only) ══ */}
          <div style={{ position: isMobile ? 'static' : 'sticky', top: 74 }}>
            <div
              onClick={() => { if (mediaItems[selectedImg]?.type !== 'video') setLightboxOpen(true); }}
              style={{
                position: 'relative', background: BG3, borderRadius: 12, overflow: 'hidden',
                aspectRatio: '1', cursor: mediaItems[selectedImg]?.type === 'video' ? 'default' : 'zoom-in',
                border: `1px solid ${BD2}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
              {mediaItems.length > 0 ? (
                mediaItems[selectedImg]?.type === 'video' ? (
                  <video
                    key={mediaItems[selectedImg].url}
                    src={mediaItems[selectedImg].url}
                    controls
                    style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                  />
                ) : (
                  <img src={thumbDetail(mediaItems[selectedImg]?.url)} alt={lot.title_zh} decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )
              ) : (
                <span style={{ fontSize: 100, lineHeight: 1, userSelect: 'none' }}>⌚</span>
              )}
              {/* ① 成色等级徽章 — 左上角叠加(从 LOT.rank 动态) */}
              {rankInfo && (
                <div style={{
                  position: 'absolute', top: 12, right: 12, padding: '6px 10px', borderRadius: 6,
                  fontSize: 14, fontWeight: 700, fontFamily: 'var(--mono,monospace)', letterSpacing: 1,
                  background: rankInfo.bg, color: rankInfo.color, textAlign: 'center', lineHeight: 1.2,
                }}>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{rankKey}</div>
                  <div style={{ fontSize: 9, fontWeight: 500 }}>ランク</div>
                </div>
              )}
              {/* 中日翻译开关 — 主图左上角(仿千旗) */}
              {(lot.title_ja || lot.title_zh) && (() => {
                const active = titleLang === 'zh' && !!(translatedTitle || lot.title_zh);
                return (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleTitleLang(); }}
                    disabled={translatingTitle}
                    title="中日翻译"
                    style={{
                      position: 'absolute', top: 12, left: 12, zIndex: 3,
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                      cursor: translatingTitle ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                      backdropFilter: 'blur(4px)', transition: 'all .15s',
                      ...(active
                        ? { background: GOLD, color: '#fff', border: `1px solid ${GOLD}`, boxShadow: '0 2px 8px rgba(184,137,42,.35)' }
                        : { background: 'rgba(255,255,255,0.85)', color: INK2, border: `1px solid ${BD}` }),
                    }}>
                    <span style={{ fontSize: 13, lineHeight: 1 }}>🌐</span>
                    {translatingTitle ? '翻译中…' : '中日翻译'}
                  </button>
                );
              })()}
              {/* メディア左右矢印ナビ */}
              {mediaItems.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); prevImg(); }} style={{
                    position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                    width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.7)', color: INK2, fontSize: 18,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background .15s', backdropFilter: 'blur(4px)',
                  }} onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.95)'} onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.7)'}>‹</button>
                  <button onClick={(e) => { e.stopPropagation(); nextImg(); }} style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.7)', color: INK2, fontSize: 18,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background .15s', backdropFilter: 'blur(4px)',
                  }} onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.95)'} onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.7)'}>›</button>
                </>
              )}
              {/* 收藏星标 — 右下角 */}
              <button onClick={async (e) => {
                e.stopPropagation();
                if (!isLoggedIn) { navigate('/login'); return; }
                try {
                  if (wishlisted) { await api.delete(`/watchlist/${id}`); setWishlisted(false); }
                  else { await api.post('/watchlist', { lot_id: id }); setWishlisted(true); }
                } catch { message.error('操作失败，请稍后重试'); }
              }} style={{
                position: 'absolute', bottom: 12, right: 12,
                width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.8)', fontSize: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all .15s', backdropFilter: 'blur(4px)',
                color: wishlisted ? RED : INK4,
              }}>{wishlisted ? '★' : '☆'}</button>
            </div>

            {/* 图片提示文字 */}
            <div style={{ textAlign: 'center', fontSize: 11, color: INK4, marginTop: 8, marginBottom: 4 }}>
              🔍 点击图片可放大查看
            </div>

            {mediaItems.length > 1 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                {mediaItems.map((media, i) => (
                  <div key={i} onClick={() => setSelectedImg(i)} style={{
                    width: 60, height: 60, borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                    background: media.type === 'video' ? '#1A1410' : BG3,
                    border: `2px solid ${selectedImg === i ? BLUE : 'transparent'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .15s', position: 'relative', flexShrink: 0,
                  }}>
                    {media.type === 'video' ? (
                      <>
                        <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.85)', userSelect: 'none' }}>▶</span>
                        {/* 動画番号バッジ */}
                        <span style={{
                          position: 'absolute', bottom: 3, right: 4,
                          fontSize: 9, color: 'rgba(255,255,255,0.7)',
                          fontFamily: 'var(--mono,monospace)',
                        }}>
                          {`動${i - images.length + 1}`}
                        </span>
                      </>
                    ) : (
                      <img src={thumbCard(media.url)} alt={`thumb-${i}`} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ══ RIGHT: Info column ══ */}
          <div>
            {/* Tags row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
              {lot.session_name && (
                <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, fontFamily: 'var(--mono,monospace)', fontWeight: 500, background: 'rgba(26,79,140,0.12)', color: BLUE }}>
                  {lot.house_name ? `${lot.house_name} · ${lot.session_name}` : lot.session_name}
                </span>
              )}
              {lot.bid_type && (
                <span style={{
                  fontSize: 10, padding: '3px 8px', borderRadius: 4, fontWeight: 700,
                  ...(lot.bid_type === 'open'
                    ? { background: 'rgba(42,102,69,0.12)', color: GREEN, border: '1px solid rgba(42,102,69,0.25)' }
                    : { background: 'rgba(140,90,10,0.12)', color: '#8C5A0A', border: '1px solid rgba(140,90,10,0.25)' }),
                }}>
                  {lot.bid_type === 'open' ? '明拍' : '🔒 暗拍'}
                </span>
              )}
              <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, fontFamily: 'var(--mono,monospace)', fontWeight: 500, background: BG3, color: INK3, border: `1px solid ${BD}` }}>
                {lot.box_no && lot.item_no ? `${lot.box_no}-${String(lot.item_no).padStart(2,'0')}` : (lot.external_lot_id || lot.box_no || lot.item_no || lot.lot_number || '')}
              </span>
              <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, fontFamily: 'var(--mono,monospace)', fontWeight: 500, background: status.bg, color: status.color }}>
                ● {status.label}
              </span>
            </div>

            {/* Title — 由「中日翻译」开关控制主显示语言 */}
            {(() => {
              const zhTitle = translatedTitle || lot.title_zh || '';
              const jaTitle = lot.title_ja || '';
              // 主标题：zh 模式优先中文(无则退日文)；ja 模式优先日文(无则退中文)
              const primary = titleLang === 'zh'
                ? (zhTitle || jaTitle)
                : (jaTitle || zhTitle);
              // 副标题：另一种语言，且与主标题不同才展示
              const secondary = titleLang === 'zh' ? jaTitle : zhTitle;
              return (
                <>
                  <h1 style={{ fontFamily: 'var(--serif,serif)', fontSize: 20, fontWeight: 600, color: INK, lineHeight: 1.4, letterSpacing: .3, marginBottom: 5 }}>
                    {primary || '暂无标题'}
                  </h1>
                  {secondary && secondary !== primary && (
                    <p style={{ fontSize: 12, color: INK3, marginBottom: 18, lineHeight: 1.6 }}>
                      {secondary}
                    </p>
                  )}
                </>
              );
            })()}

            {/* 特别提醒 — 金性提醒（贵金属拍品自动检测） */}
            {(() => {
              const titleAll = `${lot.title_ja || ''} ${lot.title_zh || ''} ${lot.category || ''}`.toLowerCase();
              const isMetal = /k\d{1,2}|pt\d{2,3}|金|ゴールド|gold|プラチナ|platinum|シルバー|silver|18k|24k|14k|9k|750|585|950|900/i.test(titleAll);
              return isMetal ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: INK3 }}>ⓘ 特别提醒:</span>
                  <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, background: 'rgba(184,137,42,0.12)', color: GOLD, border: `1px solid rgba(184,137,42,0.25)`, fontWeight: 600 }}>
                    金性提醒
                  </span>
                </div>
              ) : null;
            })()}

            {/* ② 竞价状态卡片 — 价格信息 + 倒计时 */}
            <div style={{
              background: '#fff', border: `1.5px solid ${BD}`, borderRadius: 8, padding: '14px 16px', marginBottom: 14,
              ...(priceFlash ? { animation: 'ld-flash 1.2s ease-out' } : {}),
            }}>
              {/* 暗拍提示（密封竞价场次，出价信息不公开） */}
              {lot.bid_type === 'sealed' && !isEnded && (
                <div style={{
                  background: 'rgba(140,90,10,0.08)', border: `1px solid rgba(140,90,10,0.2)`,
                  borderRadius: 4, padding: '8px 14px', marginBottom: 10,
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12, color: '#8C5A0A', lineHeight: 1.6,
                }}>
                  <span style={{ fontSize: 14 }}>🔒</span>
                  <span>暗拍（密封竞价）— 不公开他人出价与当前价，仅显示起拍价。填写您的出价即可，<b>结拍前可随时调整（可升可降）</b>，结拍后公布结果。</span>
                </div>
              )}

              {/* 当前最高出价者提示 */}
              {lot.is_top_bidder && !isEnded && (
                <div style={{
                  background: '#e8192c', color: '#fff',
                  borderRadius: 4, padding: '8px 14px', marginBottom: 10,
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, fontWeight: 600, letterSpacing: 0.2,
                }}>
                  <span style={{ fontSize: 16 }}>🏆</span>
                  <span>您暂时领先</span>
                </div>
              )}

              {/* 起拍价 */}
              {(lot.start_price_jpy || lot.start_price) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${BD3}` }}>
                  <span style={{ fontSize: 13, color: INK3 }}>起拍价：</span>
                  <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: 14, fontWeight: 500, color: INK }}>
                    {Number(lot.start_price_jpy || lot.start_price).toLocaleString('ja-JP')} <span style={{ color: INK3, fontSize: 12 }}>JPY</span>
                    <span style={{ color: INK4, fontSize: 12, marginLeft: 6 }}>（≈{Math.round(Number(lot.start_price_jpy || lot.start_price) * CNY_RATE).toLocaleString()}¥）</span>
                  </span>
                </div>
              )}

              {/* 当前价（仅明拍显示；暗拍密封竞价不公开当前价，只保留起拍价） */}
              {lot.bid_type !== 'sealed' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${BD3}` }}>
                <span style={{ fontSize: 13, color: INK3 }}>当前价：</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: 18, fontWeight: 600, color: RED }}>
                    {Number(curPrice).toLocaleString('ja-JP')} <span style={{ color: INK3, fontSize: 12, fontWeight: 400 }}>JPY</span>
                    <span style={{ color: INK4, fontSize: 12, fontWeight: 400, marginLeft: 6 }}>（≈{curPrice ? Math.round(curPrice * CNY_RATE).toLocaleString() : '—'}¥）</span>
                  </span>
                  {/* 更新按钮 — 醒目金色「刷新价格」按钮(带 ↻ 图标+文字,一眼可辨) */}
                  <button onClick={refreshLot} disabled={refreshing} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '5px 11px', border: 'none', borderRadius: 15,
                    background: refreshing ? BG2 : GOLD, color: refreshing ? INK4 : '#fff',
                    cursor: refreshing ? 'not-allowed' : 'pointer',
                    fontSize: 12, fontWeight: 700, transition: 'all .14s', flexShrink: 0, lineHeight: 1,
                    boxShadow: refreshing ? 'none' : '0 1px 5px rgba(184,137,42,0.40)',
                  }}>
                    <span style={{ display: 'inline-block', fontSize: 14, animation: refreshing ? 'ld-spin 0.7s linear infinite' : 'none' }}>↻</span>
                    {refreshing ? '刷新中' : '刷新价格'}
                  </button>
                </div>
              </div>
              )}

              {/* 现价更新时间 / 刷新失败提示（北京时间）—— 让用户明确知道价格是几点几分刷新的、有没有刷成功 */}
              {lot.bid_type !== 'sealed' && syncInfo && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '2px 0 6px', fontSize: 11 }}>
                  {refreshing ? (
                    <span style={{ color: GOLD, fontWeight: 600 }}>⟳ 正在获取最新价…</span>
                  ) : syncInfo.ok === false ? (
                    <span style={{ color: RED, fontWeight: 600 }}>
                      ⚠️ 刷新失败{syncInfo.at ? ` · 现价为上次 ${fmtBJ(syncInfo.at)} 同步结果（北京时间）` : '，请稍后再试'}
                    </span>
                  ) : (
                    <span style={{ color: INK3 }}>🕐 上次刷新 {fmtBJ(syncInfo.at)}（北京时间）</span>
                  )}
                </div>
              )}

              {/* 入札数（暗拍非公开） */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${BD3}` }}>
                <span style={{ fontSize: 13, color: INK3 }}>入札数：</span>
                <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: 14, fontWeight: 500, color: INK }}>
                  {lot.bid_type === 'sealed' ? '非公開' : (lot.bid_count > 0 ? lot.bid_count : 'ー')}
                </span>
              </div>

              {/* 今日汇率 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${BD3}` }}
                title={rateInfo?.recorded_at ? `中行 ${new Date(rateInfo.recorded_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })}` : ''}>
                <span style={{ fontSize: 13, color: INK3 }}>今日汇率：</span>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: 14, color: INK }}>
                    {CNY_RATE.toFixed(4)}
                    <span style={{ color: rateLocked ? GREEN : '#C99', fontSize: 11, marginLeft: 6 }}>
                      {rateLocked ? '结拍锁定' : '中行卖出价'}
                    </span>
                  </span>
                  {rateInfo?.recorded_at && (
                    <span style={{ fontSize: 10, color: INK4, marginTop: 2 }}>
                      （更新于 {dayjs(rateInfo.recorded_at).format('MM-DD HH:mm')} · 中国银行）
                    </span>
                  )}
                </span>
              </div>

              {/* 结束时间（北京时间） */}
              {lot.end_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${BD3}` }}>
                  <span style={{ fontSize: 13, color: INK3 }}>结束时间(北京时间)：</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: 13, color: INK }}>
                      {new Date(lot.end_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                    </span>
                    {lot.extended && (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'rgba(155,35,53,0.1)', color: RED, fontWeight: 600 }}>延长</span>
                    )}
                  </div>
                </div>
              )}

              {/* 来源 */}
              {lot.house_name && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                  <span style={{ fontSize: 13, color: INK3 }}>来源：</span>
                  <span style={{ fontSize: 13, color: INK, fontWeight: 500 }}>{lot.house_name}</span>
                </div>
              )}

              {/* ② 倒计时精确到秒，3小时内变红闪烁 */}
              <div style={{ background: BG2, borderRadius: 4, padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                <div style={{ fontSize: 10, color: INK4, fontFamily: 'var(--mono,monospace)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 14 }}>⏱</span> 残り時間
                </div>
                <div style={{
                  fontFamily: 'var(--mono,monospace)', fontSize: 16, fontWeight: 500,
                  color: urgent ? RED : INK,
                  ...(urgent ? { animation: 'ld-blink 1s infinite' } : {}),
                }}>{countdown || '—'}</div>
              </div>
            </div>

            {/* 您的委托 — 2026-07-04: 委托建好即显示(不必等真正在源站入札),给出价确认。
                代拍模型下"当前价"是源站价、不等于你的委托上限,这条 banner 让用户看到自己已出价。 */}
            {lot.my_mandate && (
              <div style={{
                background: 'rgba(42,102,69,0.08)', border: `1px solid rgba(42,102,69,0.3)`,
                borderRadius: 8, padding: '11px 14px', marginBottom: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: GREEN, marginBottom: 2 }}>✅ 您已委托本拍品</div>
                  <div style={{ fontSize: 11, color: INK3 }}>
                    授权上限 <b style={{ fontFamily: 'var(--mono,monospace)' }}>{formatJPY(Number(lot.my_mandate.max_bid_jpy))}</b>
                    <span style={{ marginLeft: 8, color: INK4 }}>· {MANDATE_STATUS_LABEL[lot.my_mandate.status] || lot.my_mandate.status}</span>
                  </div>
                </div>
                <button onClick={() => navigate('/bidding-center')} style={{
                  flexShrink: 0, padding: '7px 12px', border: `1px solid ${GREEN}`, borderRadius: 6,
                  background: '#fff', color: GREEN, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>查看我的委托</button>
              </div>
            )}

            {/* ③ 快捷加价按钮组 — 仅明拍（暗拍无递增加价；暗拍说明见上方价格区，不重复提示） */}
            {!isEnded && lot.bid_type !== 'sealed' && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: INK4, marginBottom: 8, fontFamily: 'var(--mono,monospace)' }}>
                  この商品は¥{getBidUnit(curPrice).toLocaleString('ja-JP')}単位から入札可能です
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 5 }}>
                  {quickSteps.map(step => (
                    <button key={step} onClick={() => addQuickBid(step)} style={{
                      padding: '10px 6px', background: '#fff', color: INK2,
                      border: `1px solid ${BD}`, borderRadius: 8, fontSize: 13, fontWeight: 500,
                      fontFamily: 'var(--mono,monospace)', cursor: 'pointer', textAlign: 'center',
                      lineHeight: 1, transition: 'all .15s', position: 'relative', overflow: 'hidden',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = BG2; e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(184,137,42,.15)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = BD; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <span style={{ color: GOLD, fontWeight: 700, marginRight: 1 }}>+</span>
                      {fmtStep(step)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── 已结束占位:结拍后不显示出价表单,给明确"已结束"反馈 ── */}
            {isEnded && (
              <div style={{ background: BG2, border: `1px solid ${BD}`, borderRadius: 8, padding: '20px 16px', marginBottom: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>⏱</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: INK3 }}>本拍品已结束竞拍</div>
                <div style={{ fontSize: 11, color: INK4, marginTop: 4 }}>结拍价请见上方「当前价」</div>
              </div>
            )}

            {/* ── 「立即出价」按钮:未结束且出价面板未展开时显示;点了才展开出价表单(仿千旗中古) ── */}
            {!isEnded && !bidPanelOpen && (
              <button
                onClick={() => setBidPanelOpen(true)}
                style={{
                  width: '100%', padding: '13px 20px', marginBottom: 12,
                  background: GOLD, color: '#fff', border: 'none', borderRadius: 8,
                  fontSize: 15, fontWeight: 700, cursor: 'pointer', transition: 'background .14s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#9a7122'; }}
                onMouseLeave={e => { e.currentTarget.style.background = GOLD; }}
              >
                {(lot?.my_mandate && ['pending_review', 'approved', 'queued', 'executing', 'bid_failed'].includes(lot.my_mandate.status))
                  ? '调整出价上限（加价）' : '立即出价'}
              </button>
            )}

            {/* ── Bid input section (仅未结束且已点开时显示) ── */}
            {!isEnded && bidPanelOpen && (
            <div style={{ background: '#fff', border: `1.5px solid ${BD}`, borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
              {/* 就地失败提示：出价失败不跳走，当场显示原因+最低价，已自动填好，直接再点下方按钮即可重出 */}
              {bidError && (
                <div style={{
                  background: 'rgba(200,45,45,0.06)', border: '1px solid rgba(200,45,45,0.28)',
                  borderRadius: 6, padding: '10px 12px', marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#C82D2D' }}>
                    <span style={{ fontSize: 15 }}>⚠️</span>出价未成功
                  </div>
                  <div style={{ fontSize: 11, color: INK3, lineHeight: 1.7, marginTop: 4 }}>
                    {bidError.msg}
                    {bidError.minBid ? <><br /><b>已为你填入最低可出价 ¥{Number(bidError.minBid).toLocaleString('ja-JP')}，点下方「立即委托竞拍」即可重新出价</b></> : null}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: INK3, fontFamily: 'var(--mono,monospace)' }}>入札額</span>
                <span style={{ fontSize: 10, color: INK4, fontFamily: 'var(--mono,monospace)' }}>
                  最小単位：¥{getBidUnit(bidVal || curPrice).toLocaleString('ja-JP')}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <input
                  type="number"
                  value={maxBid}
                  onChange={(e) => setMaxBid(e.target.value)}
                  placeholder="0"
                  style={{
                    flex: 1, minWidth: 0, padding: '10px 12px', border: `1.5px solid ${BD}`, borderRadius: 4,
                    fontFamily: 'var(--mono,monospace)', fontSize: 20, fontWeight: 500,
                    color: INK, background: BG, textAlign: 'right', outline: 'none',
                    transition: 'border-color .14s',
                  }}
                  onFocus={e => e.currentTarget.style.borderColor = GOLD}
                  onBlur={e => e.currentTarget.style.borderColor = BD}
                />
                <span style={{ fontSize: 14, color: INK3, flexShrink: 0, fontFamily: 'var(--mono,monospace)' }}>円</span>
              </div>

              {/* ── 出价策略 ── */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: INK3, marginBottom: 6 }}>出价策略</div>
                <div style={{ display: 'flex', gap: 0, borderRadius: 6, border: `1px solid ${BD}`, overflow: 'hidden' }}>
                  {[
                    { key: 'immediate', label: '立即出价', desc: '提交后马上出价' },
                    { key: 'scheduled', label: '预约出价', desc: '定时自动出价' },
                    { key: 'manual', label: '手动出价', desc: '自己选择时机' },
                  ].map((opt, i) => (
                    <button
                      key={opt.key}
                      onClick={() => setSubmitStrategy(opt.key)}
                      style={{
                        flex: 1, padding: '8px 4px', border: 'none', cursor: 'pointer',
                        fontSize: 11, fontWeight: submitStrategy === opt.key ? 600 : 400,
                        color: submitStrategy === opt.key ? GOLD : INK3,
                        background: submitStrategy === opt.key ? 'rgba(184,137,42,.08)' : '#fff',
                        borderRight: i < 2 ? `1px solid ${BD}` : 'none',
                        transition: 'all .12s',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {submitStrategy !== 'immediate' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11, color: INK3 }}>
                    <span>{submitStrategy === 'scheduled' ? '结束前' : '兜底：结束前'}</span>
                    <select
                      value={submitBeforeMins}
                      onChange={e => setSubmitBeforeMins(Number(e.target.value))}
                      style={{
                        padding: '4px 8px', borderRadius: 4, border: `1px solid ${BD}`,
                        fontSize: 12, color: INK2, background: '#fff', cursor: 'pointer',
                      }}
                    >
                      {[1, 2, 3, 5, 10, 15, 30, 60].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <span>分钟自动提交</span>
                  </div>
                )}
                {submitStrategy === 'manual' && (
                  <div style={{ fontSize: 10, color: INK4, marginTop: 4 }}>
                    提交后在竞拍中心随时可点"立即出价"，若未手动提交则兜底自动出价
                  </div>
                )}
              </div>

              <button
                onClick={handleBidSubmit}
                disabled={submitting || submitted || bidBtnInactive}
                style={{
                  width: '100%', padding: '12px 20px',
                  background: bidBtnInactive ? BD : GOLD,
                  color: bidBtnInactive ? INK4 : '#fff',
                  border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 600,
                  cursor: bidBtnInactive ? 'not-allowed' : 'pointer',
                  transition: 'all .14s', whiteSpace: 'nowrap', marginBottom: 4,
                }}
                onMouseEnter={e => { if (!bidBtnInactive) e.currentTarget.style.background = '#9a7122'; }}
                onMouseLeave={e => { if (!bidBtnInactive) e.currentTarget.style.background = GOLD; }}
              >
                {submitting ? '提交中…' : isEnded ? '已结束' : phoneMissing ? '📱 绑定手机号后出价' : !kycVerified ? '待实名认证' : balanceInsufficient ? '余额不足'
                  : (lot?.my_mandate && ['pending_review', 'approved', 'queued', 'executing', 'bid_failed'].includes(lot.my_mandate.status)) ? '✅ 确认加价（调整上限）'
                  : submitStrategy === 'immediate' ? '✅ 立即委托竞拍'
                  : submitStrategy === 'scheduled' ? '⏰ 预约出价'
                  : '🎯 设置手动出价'}
              </button>

              {/* 合作伙伴推荐入口:暂时对所有人关闭(2026-07 用户要求下线);恢复时去掉 false && 即可。原本仅 promoter/agent 渲染 */}
              {false && (user?.distribution_role === 'promoter' || user?.distribution_role === 'agent') && (
                <button
                  onClick={() => navigate(`/partner/share?lotId=${lot?.id || params.id}`)}
                  style={{
                    width: '100%', padding: '9px 20px', marginBottom: 8,
                    background: 'transparent', color: GOLD, border: `1px dashed ${GOLD}`,
                    borderRadius: 4, fontSize: 13, cursor: 'pointer',
                  }}
                >📣 推荐此拍品（生成推广海报）</button>
              )}

              {/* ④ 费用实时预算 */}
              {bidVal > 0 && (
                <div style={{ fontSize: 11 }}>
                  {[
                    ['落槌价（授权上限）', formatJPY(bidVal)],
                    [`消費税（${Math.round(TAX_RATE * 100)}%）`, formatJPY(taxJpy)],
                    [`拍卖行手续费（${(SERVICE_FEE_RATE * 100).toFixed(1)}%）`, formatJPY(svcFeeJpy)],
                  ].map(([k, v]) => (
                    <div key={k} style={{
                      display: 'flex', justifyContent: 'space-between', padding: '4px 0',
                      color: INK3, borderBottom: `1px solid ${BD3}`,
                    }}>
                      <span>{k}</span>
                      <span style={{ fontFamily: 'var(--mono,monospace)' }}>{v}</span>
                    </div>
                  ))}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', padding: '6px 0 2px',
                    color: INK, fontWeight: 600, borderBottom: `1px solid ${BD3}`,
                  }}>
                    <span>合计费用（日元）</span>
                    <span style={{ fontFamily: 'var(--mono,monospace)' }}>{formatJPY(bidVal + taxJpy + svcFeeJpy)}</span>
                  </div>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', padding: '4px 0 6px',
                    color: GOLD, fontWeight: 600, borderBottom: `1px solid ${BD3}`,
                  }}>
                    <span>合计费用（人民币）</span>
                    <span style={{ fontFamily: 'var(--mono,monospace)' }}>≈ ¥{Math.round((bidVal + taxJpy + svcFeeJpy) * CNY_RATE).toLocaleString()} CNY</span>
                  </div>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', padding: '3px 0 5px',
                    color: INK4, fontSize: 10, borderBottom: `1px solid ${BD3}`,
                  }}>
                    <span>中行卖出价</span>
                    <span style={{ fontFamily: 'var(--mono,monospace)' }}>1 JPY = {CNY_RATE.toFixed(4)} CNY</span>
                  </div>
                  {/* 预冻结保证金 — deposit_required=0 时显示"免保证金" */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 0 4px', fontWeight: 600, color: INK,
                  }}>
                    <span>预冻结保证金约</span>
                    {depositRequired ? (
                      <span style={{ fontFamily: 'var(--mono,monospace)', color: balanceInsufficient ? RED : GOLD }}>
                        ¥{freezeCny.toLocaleString()} CNY
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: '#28a745', fontWeight: 600 }}>免保证金 ✓</span>
                    )}
                  </div>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '2px 0', color: INK4, fontSize: 10,
                    borderTop: `1px dashed ${BD2}`,
                  }}>
                    <span>可用余额</span>
                    <span style={{ fontFamily: 'var(--mono,monospace)', color: balanceInsufficient ? RED : GREEN }}>
                      ¥{userBalance.toLocaleString()} CNY
                    </span>
                  </div>
                  {/* ④ 余额不足 — 醒目充值提示 */}
                  {balanceInsufficient && (
                    <Link to="/deposit" style={{ textDecoration: 'none', display: 'block', marginTop: 8 }}>
                      <div style={{
                        background: 'rgba(155,35,53,0.06)', border: `1px solid rgba(155,35,53,0.2)`,
                        borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', cursor: 'pointer', transition: 'all .14s',
                      }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: RED, marginBottom: 2 }}>
                            ⚠ 保证金不足，无法提交委托
                          </div>
                          <div style={{ fontSize: 10, color: INK3 }}>
                            还需充值 ¥{(freezeCny - userBalance).toLocaleString()} CNY
                          </div>
                        </div>
                        <div style={{
                          padding: '6px 14px', background: RED, color: '#fff',
                          borderRadius: 4, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                        }}>立即充值 →</div>
                      </div>
                    </Link>
                  )}
                </div>
              )}

              {!isLoggedIn && (
                <div style={{ marginTop: 8, color: RED, fontSize: 10 }}>
                  ⚠ 请先 <Link to="/login" style={{ color: RED }}>登录</Link> 或 <Link to="/register" style={{ color: RED }}>注册</Link> 后提交委托
                </div>
              )}
            </div>
            )}

            {/* 持久化绿色 banner：重新打开 popup 时，有委托记录且当前领先 */}
            {!submitted && lot.is_top_bidder && lot.my_max_bid_jpy && !isEnded && (
              <div style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
                <div style={{
                  background: GREEN, color: '#fff', padding: '10px 16px',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, fontWeight: 600,
                }}>
                  <span style={{ fontSize: 18 }}>🏆</span>
                  入札成功！您当前领先
                </div>
                <div style={{
                  padding: '14px 16px', textAlign: 'center',
                  background: 'rgba(42,102,69,0.07)',
                  border: '1px solid rgba(42,102,69,0.2)',
                  borderTop: 'none',
                }}>
                  <p style={{ fontSize: 11, color: INK3, lineHeight: 1.9, margin: '0 0 10px' }}>
                    授权出价 {formatJPY(Number(lot.my_max_bid_jpy))}<br />
                    如有新竞买人超出，系统将在授权范围内自动加价
                  </p>
                  <button onClick={() => navigate('/mandates')} style={{
                    padding: '7px 18px', background: GREEN,
                    border: 'none', borderRadius: 4, color: '#fff', fontSize: 12, cursor: 'pointer',
                  }}>查看我的委托</button>
                </div>
              </div>
            )}

            {submitted && (() => {
              const o = bidOutcome || {};
              // 自动审核 + 入札成功
              if (o.autoReview && o.bidSuccess) {
                const leading = o.isLeading;
                return (
                  <div style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
                    {/* 顶部全宽色条 — 替代不可见的 toast */}
                    <div style={{
                      background: leading ? GREEN : '#C86A00',
                      color: '#fff', padding: '10px 16px',
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 13, fontWeight: 600,
                    }}>
                      <span style={{ fontSize: 18 }}>{leading ? '🏆' : '⚡'}</span>
                      {leading ? '入札成功！您当前领先' : '入札成功，但已被其他竞买人超越'}
                    </div>
                    <div style={{
                      padding: '14px 16px', textAlign: 'center',
                      background: leading ? 'rgba(42,102,69,0.07)' : 'rgba(200,106,0,0.07)',
                      border: `1px solid ${leading ? 'rgba(42,102,69,0.2)' : 'rgba(200,106,0,0.2)'}`,
                      borderTop: 'none',
                    }}>
                      <p style={{ fontSize: 11, color: INK3, lineHeight: 1.9, margin: '0 0 10px' }}>
                        授权出价 {formatJPY(Number(maxBid))}<br />
                        {leading ? '如有新竞买人超出，系统将在授权范围内自动加价' : '请持续关注，或提高授权金额重新委托'}
                      </p>
                      <button onClick={() => navigate('/mandates')} style={{
                        padding: '7px 18px', background: leading ? GREEN : '#C86A00',
                        border: 'none', borderRadius: 4, color: '#fff', fontSize: 12, cursor: 'pointer',
                      }}>查看我的委托</button>
                    </div>
                  </div>
                );
              }
              // 人工审核 or 不支持自动入札
              return (
                <div style={{
                  padding: '20px 16px', background: 'rgba(42,102,69,0.08)', border: '1px solid rgba(42,102,69,0.15)',
                  borderRadius: 8, textAlign: 'center', marginBottom: 12,
                }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                  <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 16, color: GREEN, letterSpacing: 2, marginBottom: 6 }}>委托已提交</div>
                  <p style={{ fontSize: 11, color: INK3, lineHeight: 1.8 }}>
                    最高授权出价 {formatJPY(Number(maxBid))}<br />
                    运营将在2小时内审核
                  </p>
                  <button onClick={() => navigate('/mandates')} style={{
                    marginTop: 10, padding: '7px 18px', background: GOLD, border: 'none',
                    borderRadius: 4, color: '#fff', fontSize: 12, cursor: 'pointer',
                  }}>查看我的委托</button>
                </div>
              );
            })()}

            {/* Sub action buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <button onClick={async () => {
                if (!isLoggedIn) { navigate('/login'); return; }
                try {
                  if (wishlisted) { await api.delete(`/watchlist/${id}`); setWishlisted(false); message.success('已取消关注'); }
                  else { await api.post('/watchlist', { lot_id: id }); setWishlisted(true); message.success('已加入关注'); }
                } catch { message.error('操作失败，请稍后重试'); }
              }} style={{
                flex: 1, minWidth: isMobile ? 'calc(50% - 4px)' : 0,
                padding: '9px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                transition: 'all .14s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                border: `1.5px solid ${wishlisted ? RED : BD}`,
                background: wishlisted ? 'rgba(155,35,53,0.07)' : '#fff',
                color: wishlisted ? RED : INK2,
              }}>{wishlisted ? '♥ 已关注' : '♡ 加入关注'}</button>
              <button onClick={() => {
                // 回到访问的上一页(列表页数/筛选/滚动都在,不弹回拍场);无站内历史(外链直达)才兜底拍场总览。
                if (window.history.length > 1) navigate(-1);
                else navigate('/auction-hub');
              }} style={{
                flex: 1, minWidth: isMobile ? 'calc(50% - 4px)' : 0,
                padding: '9px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                border: `1.5px solid ${BD}`, background: '#fff', color: INK2,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}>← 返回上一页</button>
              <button onClick={() => navigate('/mandates')} style={{
                flex: 1, minWidth: isMobile ? '100%' : 0,
                padding: '9px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                border: `1.5px solid ${BD}`, background: '#fff', color: INK2,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}>📋 我的委托</button>
            </div>

          </div>
        </div>

        {/* ══ Full-width sections below ══ */}

        {/* ⑤ 商品概要 / 新手必读 / 选品须知 / 常见问题 Tab */}
        <div style={{ background: '#fff', border: `1px solid ${BD2}`, borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ display: 'flex', borderBottom: `1px solid ${BD2}` }}>
            {[
              { key: 'overview', label: '商品概要' },
              { key: 'guide', label: '新手必读' },
              { key: 'selection', label: '选品须知' },
              { key: 'faq', label: '常见问题' },
            ].map(t => (
              <button key={t.key} onClick={() => setInfoTab(t.key)} style={{
                flex: 1, padding: '12px 4px', border: 'none', cursor: 'pointer',
                fontSize: isMobile ? 11 : 12, fontWeight: infoTab === t.key ? 600 : 400,
                color: infoTab === t.key ? GOLD : INK3,
                background: 'transparent',
                borderBottom: infoTab === t.key ? `2px solid ${GOLD}` : '2px solid transparent',
                transition: 'all .12s',
              }}>
                {t.label}
              </button>
            ))}
          </div>
          {/* ── 商品概要 ── */}
          {infoTab === 'overview' && (<div>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${BD2}`, fontSize: 12, fontWeight: 600, color: INK, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: BLUE, fontSize: 10 }}>●</span> 商品情報
            </span>
            {!Object.keys(translatedSpecs).length && (
              <button onClick={async () => {
                setTranslatingSpecs(true);
                try {
                  const vals = Object.entries(itemInfo)
                    .filter(([, v]) => typeof v === 'string' && v.trim() && /[\u3040-\u30FF\u4E00-\u9FFF]/.test(v))
                    .map(([k, v]) => ({ k, v }));
                  if (vals.length) {
                    const textBlock = vals.map(({ k, v }) => `[${k}]: ${v}`).join('\n');
                    const res = await api.post('/ai/translate', { text: textBlock, direction: 'ja2zh' });
                    const translated = {};
                    (res.data.translated || '').split('\n').forEach(line => {
                      const m = line.match(/^\[(.+?)\]:\s*(.+)/);
                      if (m) translated[m[1]] = m[2];
                    });
                    setTranslatedSpecs(translated);
                  }
                } catch { message.error('翻译服务暂不可用'); }
                setTranslatingSpecs(false);
              }} disabled={translatingSpecs} style={{
                padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 500,
                border: `1px solid ${GOLD}`, cursor: translatingSpecs ? 'wait' : 'pointer',
                background: 'rgba(184,137,42,.08)', color: GOLD,
              }}>
                {translatingSpecs ? '翻译中...' : '一键翻译'}
              </button>
            )}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <tbody>
              {(() => {
                const cat = (lot.category || '').toLowerCase();
                /* ⑤ 按品类动态生成规格项 — 手表/珠宝/包包 各不同 */
                const catFields = cat === 'watch' || cat === '腕表' ? [
                  ['ブランド / 品牌', lot.brand || itemInfo['ブランド']],
                  ['型番 / 型号', itemInfo['型番'] || itemInfo['Ref.'] || lot.model],
                  ['シリアル / 序列号', itemInfo['シリアル'] || itemInfo['Serial']],
                  ['商品ランク / 成色', rankKey && rankInfo ? null : (lot.rank || lot.condition)],
                  ['ムーブメント / 机芯', itemInfo['ムーブメント'] || itemInfo['機芯'] || itemInfo['Cal.']],
                  ['ケース素材 / 表壳材质', itemInfo['ケース素材'] || itemInfo['素材'] || lot.material],
                  ['ケースサイズ / 表径', itemInfo['ケースサイズ'] || itemInfo['サイズ'] || lot.size],
                  ['文字盤 / 表盘', itemInfo['文字盤'] || itemInfo['ダイアル']],
                  ['ベルト / 表带', itemInfo['ベルト'] || itemInfo['ブレスレット']],
                  ['防水性能', itemInfo['防水'] || itemInfo['防水性能']],
                  ['年式 / 年份', itemInfo['年式'] || itemInfo['製造年'] || lot.year],
                  ['商品程度', itemInfo['商品程度'] || lot.condition_detail],
                  ['附属品', itemInfo['附属品'] || itemInfo['付属品'] || lot.accessories],
                ] : cat === 'jewelry' || cat === 'jewel' || cat === '珠宝' ? [
                  ['ブランド / 品牌', lot.brand || itemInfo['ブランド']],
                  ['シリアル / 型号', itemInfo['シリアル'] || itemInfo['型番'] || lot.model],
                  ['商品ランク / 成色', rankKey && rankInfo ? null : (lot.rank || lot.condition)],
                  ['ジュエリー素材 / 材质', itemInfo['ジュエリー素材'] || itemInfo['素材'] || lot.material],
                  ['主石 / 主石', itemInfo['主石'] || itemInfo['宝石'] || itemInfo['メインストーン'] || itemInfo['石目']],
                  ['脇石 / 配石', itemInfo['脇石']],
                  ['カラット / 克拉', itemInfo['カラット'] || itemInfo['ct'] || itemInfo['karat']],
                  ['サイズ / 尺寸', itemInfo['サイズ'] || itemInfo['号数'] || lot.size],
                  ['総重量 / 重量', itemInfo['総重量'] || itemInfo['重量'] || lot.weight],
                  ['商品程度 / 瑕疵', itemInfo['商品程度'] || lot.condition_detail],
                  ['附属品', itemInfo['附属品'] || itemInfo['付属品'] || lot.accessories],
                ] : cat === 'bag' || cat === '名包' || cat === '包' ? [
                  ['ブランド / 品牌', lot.brand || itemInfo['ブランド']],
                  ['型番 / 型号', itemInfo['型番'] || itemInfo['シリアル'] || lot.model],
                  ['商品ランク / 成色', rankKey && rankInfo ? null : (lot.rank || lot.condition)],
                  ['素材 / 材质', itemInfo['素材'] || lot.material],
                  ['カラー / 颜色', itemInfo['カラー'] || itemInfo['色'] || itemInfo['颜色']],
                  ['サイズ / 尺寸', itemInfo['サイズ'] || lot.size],
                  ['金具 / 五金', itemInfo['金具'] || itemInfo['ハードウェア']],
                  ['刻印 / 印记', itemInfo['刻印'] || itemInfo['シリアル']],
                  ['商品程度', itemInfo['商品程度'] || lot.condition_detail],
                  ['附属品', itemInfo['附属品'] || itemInfo['付属品'] || lot.accessories],
                ] : [
                  ['ブランド / 品牌', lot.brand || itemInfo['ブランド'] || lot.title_zh?.split(' ')[0]],
                  ['シリアル / 型号', itemInfo['シリアル'] || itemInfo['型番'] || lot.model],
                  ['商品ランク / 成色', rankKey && rankInfo ? null : (lot.rank || lot.condition)],
                  ['素材 / 材质', itemInfo['素材'] || lot.material],
                  ['サイズ / 尺寸', itemInfo['サイズ'] || lot.size],
                  ['総重量 / 重量', itemInfo['総重量'] || itemInfo['重量'] || lot.weight],
                  ['商品程度', itemInfo['商品程度'] || lot.condition_detail],
                  ['附属品', itemInfo['附属品'] || itemInfo['付属品'] || lot.accessories],
                ];
                const auctionFields = [
                  ['拍卖行', lot.house_name],
                  ['场次名称', lot.session_name],
                  ['拍品编号', lot.external_lot_id || lot.lot_number],
                  ['箱号 / 商品番号', lot.box_no && lot.item_no ? `${lot.box_no} / ${String(lot.item_no).padStart(2,'0')}` : (lot.box_no || null)],
                  ['入札数', ['rkauction', 'eco'].includes(lot.house_code) ? '非公開' : (lot.bid_count != null ? `${lot.bid_count} 次` : 'ー')],
                  ['起拍价', formatJPY(lot.start_price_jpy)],
                  ['估价', lot.estimate_low_jpy && lot.estimate_high_jpy ? `${formatJPY(lot.estimate_low_jpy)} ~ ${formatJPY(lot.estimate_high_jpy)}` : null],
                  ['拍卖结束', lot.end_at ? dayjs(lot.end_at).format('YYYY年MM月DD日 HH:mm') : null],
                ];
                return [...catFields, ...auctionFields];
              })().filter(([, v]) => v && !['0','-','false','ー'].includes(String(v).trim())).map(([k, v]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${BD3}` }}>
                  <th style={{ width: isMobile ? 88 : 150, padding: isMobile ? '8px 10px' : '10px 16px', fontSize: isMobile ? 11 : 12, fontWeight: 600, color: INK2, textAlign: 'left', background: BG2, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</th>
                  <td style={{ padding: isMobile ? '8px 10px' : '10px 16px', fontSize: isMobile ? 12 : 13, color: INK, lineHeight: 1.6, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{(v && typeof v === 'object') ? JSON.stringify(v) : v}</td>
                </tr>
              ))}
              {/* ⑤ 商品ランク — 带颜色等级pip */}
              {rankKey && rankInfo && (
                <tr style={{ borderBottom: `1px solid ${BD3}` }}>
                  <th style={{ width: isMobile ? 88 : 150, padding: isMobile ? '8px 10px' : '10px 16px', fontSize: isMobile ? 11 : 12, fontWeight: 600, color: INK2, textAlign: 'left', background: BG2, whiteSpace: 'nowrap', verticalAlign: 'top' }}>商品ランク / 成色</th>
                  <td style={{ padding: isMobile ? '8px 10px' : '10px 16px', fontSize: isMobile ? 12 : 13, color: INK, lineHeight: 1.6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-block', width: 20, height: 20, borderRadius: 4,
                      fontSize: 11, fontWeight: 700, textAlign: 'center', lineHeight: '20px',
                      fontFamily: 'var(--mono,monospace)', flexShrink: 0,
                      background: rankInfo.bg, color: rankInfo.color,
                    }}>{rankKey}</span>
                    <span style={{ fontSize: 11, color: INK3 }}>{rankInfo.label}</span>
                  </td>
                </tr>
              )}
              {/* raw_data extra fields not already covered */}
              {Object.entries(itemInfo)
                .filter(([k, v]) => ![
                  // 基本フィールド（全カテゴリ共通）
                  'ブランド','シリアル','型番','Ref.','Serial',
                  '素材','材质','ジュエリー素材',
                  'サイズ','尺寸','号数','ケースサイズ',
                  '総重量','重量',
                  '附属品','付属品',
                  // 商品状態・ランク（catFields + rankRow で表示済み）
                  '商品程度','商品ランク',
                  // 時計固有
                  'ムーブメント','機芯','Cal.',
                  'ケース素材','文字盤','ダイアル','ベルト','ブレスレット',
                  '防水','防水性能','年式','製造年',
                  // ジュエリー固有
                  '石目','宝石','メインストーン','カラット','ct','主石','脇石',
                  // バッグ固有
                  'カラー','色','颜色','金具','ハードウェア','刻印',
                ].includes(k)
                  && !isInternalSpecKey(k, v)
                  && ((sv) => sv && !['0','false','-','ー','null','undefined'].includes(sv))(String(v).trim()))
                .map(([k, v]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${BD3}` }}>
                  <th style={{ width: isMobile ? 88 : 150, padding: isMobile ? '8px 10px' : '10px 16px', fontSize: isMobile ? 11 : 12, fontWeight: 600, color: INK2, textAlign: 'left', background: BG2, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{SPEC_CONTENT_LABELS[k] || k}</th>
                  <td style={{ padding: isMobile ? '8px 10px' : '10px 16px', fontSize: isMobile ? 12 : 13, color: INK, lineHeight: 1.6, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translatedSpecs[k] ? <><span>{translatedSpecs[k]}</span><br/><span style={{ fontSize: 10, color: INK4 }}>{String(v)}</span></> : String(v)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* ── 市场参考价（AI估价）已按需求下线，暂不显示 ── */}
          {/* ── Description (inside overview tab) ── */}
          {lot.description && (
            <div style={{ borderTop: `1px solid ${BD2}` }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${BD2}`, fontSize: 12, fontWeight: 600, color: INK, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: BLUE, fontSize: 10 }}>●</span> 商品説明 / 藏品说明
                </span>
                {!translatedDesc && (
                  <button onClick={async () => {
                    setTranslating(true);
                    try {
                      const res = await api.post('/ai/translate', { text: lot.description, direction: 'ja2zh' });
                      setTranslatedDesc(res.data.translated);
                    } catch { message.error('翻译服务暂不可用'); }
                    setTranslating(false);
                  }} disabled={translating} style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 500,
                    border: `1px solid ${GOLD}`, cursor: translating ? 'wait' : 'pointer',
                    background: 'rgba(184,137,42,.08)', color: GOLD,
                  }}>
                    {translating ? '翻译中...' : 'AI 翻译'}
                  </button>
                )}
              </div>
              <div style={{ padding: 16, fontSize: 13, color: INK2, lineHeight: 1.85 }}>
                {lot.description}
              </div>
              {translatedDesc && (
                <div style={{ padding: '12px 16px', borderTop: `1px dashed ${BD2}`, background: 'rgba(184,137,42,.04)' }}>
                  <div style={{ fontSize: 10, color: GOLD, fontWeight: 600, marginBottom: 6 }}>AI 中文翻译</div>
                  <div style={{ fontSize: 13, color: INK2, lineHeight: 1.85 }}>{translatedDesc}</div>
                </div>
              )}
            </div>
          )}
          {/* 服务费用说明 */}
          <div style={{ margin: '12px 16px', padding: '12px 14px', background: BG, borderRadius: 6, borderLeft: `3px solid ${GOLD}` }}>
            <div style={{ fontSize: 12, color: INK3, lineHeight: 1.9 }}>
              <div>云川中古与日本拍卖会实时同步，页面金额已包含日本消费税及拍卖会手续费。</div>
              <div>除固定服务费外，无任何隐形消费。</div>
              <div>中拍成功代拍费200元，未中拍代拍费100元，物流运输服务费100元。</div>
              <div>中拍后可根据需求提供鉴定、改圈、维修、运输等配套服务。</div>
            </div>
          </div>
          </div>)}
          {/* ── 新手必读（参拍须知内容） ── */}
          {infoTab === 'guide' && (
            <div style={{ padding: '16px', fontSize: 13, color: INK3, lineHeight: 1.85 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>1. 平台服务说明</h3>
              <p style={{ marginBottom: 4 }}>云川中古提供日本拍卖会、中古市场、质店及相关平台的代拍服务。</p>
              <p style={{ marginBottom: 4 }}>本平台与日本拍卖会实时同步，商品价格、竞拍状态、结束时间等信息以拍卖会实际页面为准。</p>
              <p style={{ marginBottom: 4 }}>客户可根据页面显示价格提交委托出价，本平台将按照客户确认的金额进行代拍。</p>
              <p style={{ marginBottom: 12 }}>代拍属于客户委托购买行为，并非普通现货销售。请客户确认好商品信息、预算及拍卖时间后再提交委托。</p>

              <div style={{ height: 1, background: '#EAE5DC', margin: '16px 0' }} />

              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>2. 保证金及出价规则</h3>
              <p style={{ marginBottom: 4 }}>部分商品委托前需支付保证金。</p>
              <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>未中拍：保证金可退回，或保留为余额下次使用。</li>
                <li>中拍成功：保证金自动抵扣尾款。</li>
                <li>中拍后取消：保证金不予退还。</li>
              </ul>
              <p style={{ marginBottom: 4 }}>客户提交委托后，即代表授权本平台按照客户确认金额参与竞拍。</p>
              <p style={{ marginBottom: 4 }}>委托一旦提交，原则上不可随意取消、修改、撤回或反悔。</p>
              <p style={{ marginBottom: 12 }}>如中拍后客户因个人原因不要、拒绝付款或逾期付款，保证金不予退还，并可能影响后续代拍资格。</p>

              <div style={{ height: 1, background: '#EAE5DC', margin: '16px 0' }} />

              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>3. 暗拍说明</h3>
              <p style={{ marginBottom: 4 }}>部分拍卖会或商品属于暗拍形式。</p>
              <p style={{ marginBottom: 4 }}>暗拍不是实时公开竞价，客户无法看到其他买家的出价，也无法确认当前最高价。</p>
              <p style={{ marginBottom: 4 }}>客户需提前填写自己可接受的最高委托价，本平台将按照客户确认金额提交。</p>
              <p style={{ marginBottom: 4 }}>暗拍提交后，原则上无法取消、修改或撤回。</p>
              <p style={{ marginBottom: 12 }}>最终是否中拍，以拍卖会公布结果为准，本平台不保证一定中拍。</p>

              <div style={{ height: 1, background: '#EAE5DC', margin: '16px 0' }} />

              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>4. 竞拍结果及低价商讨</h3>
              <p style={{ marginBottom: 4 }}>竞拍过程中可能出现以下情况：</p>
              <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>其他买家出价更高</li>
                <li>拍卖会自动延时</li>
                <li>系统刷新延迟</li>
                <li>商品临时撤拍</li>
                <li>卖家取消交易</li>
                <li>拍卖会判定成交无效</li>
                <li>商品信息临时变更</li>
              </ul>
              <p style={{ marginBottom: 4 }}>最终结果以拍卖会或平台实际成交记录为准。</p>
              <p style={{ marginBottom: 4 }}>部分拍卖会存在低价商讨机制。当落槌价格明显低于卖家或拍卖会预期时，即使系统显示中拍，也可能需要等待卖家最终确认。</p>
              <p style={{ marginBottom: 4 }}>商讨结果可能包括：</p>
              <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>原价成交</li>
                <li>卖家要求加价成交</li>
                <li>卖家拒绝成交</li>
                <li>拍卖会判定成交无效</li>
              </ul>
              <p style={{ marginBottom: 4 }}>如遇低价商讨，本平台会第一时间与客户沟通。若客户不接受加价，或卖家拒绝成交，则该商品视为未成交。</p>
              <p style={{ marginBottom: 12 }}>低价商讨属于拍卖会规则，并非本平台额外加价或隐形收费。</p>

              <div style={{ height: 1, background: '#EAE5DC', margin: '16px 0' }} />

              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>5. 账号安全规则</h3>
              <p style={{ marginBottom: 4 }}>云川中古代拍账号仅限平台内部使用。</p>
              <p style={{ marginBottom: 4 }}>客户不得借用、租用、转借、转租、共享、买卖本平台账号。</p>
              <p style={{ marginBottom: 4 }}>客户不得私自登录、代替操作、传播后台截图、泄露拍卖会信息，或将账号相关内容提供给第三方。</p>
              <p style={{ marginBottom: 4 }}>如因客户违规行为导致账号异常、封禁、纠纷、损失或法律责任，均由客户自行承担。</p>
              <p>严重违规者，本平台有权终止服务，并拒绝后续委托。</p>
            </div>
          )}
          {/* ── 选品须知内容 ── */}
          {infoTab === 'selection' && (
            <div style={{ padding: '16px', fontSize: 13, color: INK3, lineHeight: 1.85 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>1. 商品状态说明</h3>
              <p style={{ marginBottom: 4 }}>代拍商品多为日本中古商品，并非全新品。</p>
              <p style={{ marginBottom: 4 }}>中古商品可能存在轻微划痕、磨损、氧化、变色、污渍、维修痕迹、尺寸误差、重量误差、附件缺失、盒证不全等情况。</p>
              <p style={{ marginBottom: 4 }}>商品状态以拍卖会、卖家或平台公开图片及描述为准。</p>
              <p style={{ marginBottom: 12 }}>客户如对品相、尺寸、颜色、重量、附件、证书等要求较高，请在委托前提前说明并确认。</p>

              <div style={{ height: 1, background: '#EAE5DC', margin: '16px 0' }} />

              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>2. 鉴定及配套服务</h3>
              <p style={{ marginBottom: 4 }}>云川中古除代拍服务外，也可根据客户需求提供后续配套服务，包括但不限于：</p>
              <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>珠宝鉴定证书</li>
                <li>宝石鉴定</li>
                <li>改圈改号</li>
                <li>清洗保养</li>
                <li>维修翻新</li>
                <li>重新电镀</li>
                <li>包装加固</li>
                <li>国际运输协助</li>
              </ul>
              <p>以上配套服务根据实际需求另行报价，客户确认后安排。</p>
            </div>
          )}
          {/* ── 常见问题内容 ── */}
          {infoTab === 'faq' && (
            <div style={{ padding: '16px', fontSize: 13, color: INK3, lineHeight: 1.85 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>1. 费用说明</h3>
              <p style={{ marginBottom: 4 }}>云川中古坚持费用透明。</p>
              <p style={{ marginBottom: 4 }}>页面显示金额已包含：</p>
              <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>日本消费税</li>
                <li>拍卖会手续费</li>
              </ul>
              <p style={{ marginBottom: 4 }}>除明确标注的固定服务费外，本平台不收取任何隐形费用。</p>
              <p style={{ marginBottom: 4 }}>固定服务费如下：</p>
              <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>中拍成功：代拍服务费200元</li>
                <li>未中拍：代拍服务费100元</li>
                <li>物流运输服务费100元</li>
              </ul>
              <div style={{ margin: '10px 0 12px', padding: '10px 12px', background: BG, borderRadius: 6, borderLeft: `3px solid ${GOLD}` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: INK, marginBottom: 4 }}>最终到手价公式</div>
                <div style={{ fontSize: 12, color: INK3, lineHeight: 1.8 }}>
                  最终金额 = 落槌价 + 日本消费税 + 拍卖行手续费 + 国际/国内运费 + 代拍服务费，按实时汇率结算
                </div>
              </div>
              <p style={{ marginBottom: 4 }}>大陆及香港地区基础清关费用包含在物流运输服务费内。</p>
              <p style={{ marginBottom: 12 }}>如需发往其他国家或地区，国际运费差价及当地关税由客户自行承担。</p>

              <div style={{ height: 1, background: '#EAE5DC', margin: '16px 0' }} />

              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>2. 付款、取消与退换</h3>
              <p style={{ marginBottom: 4 }}>商品中拍后，客户需在规定时间内补齐尾款。</p>
              <p style={{ marginBottom: 4 }}>尾款金额以实际成交价、页面显示金额、拍卖会账单及实际产生费用为准。</p>
              <p style={{ marginBottom: 4 }}>本平台除固定服务费外，不额外加收隐藏费用。</p>
              <p style={{ marginBottom: 4 }}>代拍商品中拍后，原则上不支持取消、退货、换货。</p>
              <p style={{ marginBottom: 4 }}>以下情况一般不作为取消或退货理由：</p>
              <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>觉得拍贵了</li>
                <li>临时不想要了</li>
                <li>尺寸不合适</li>
                <li>图片色差</li>
                <li>轻微瑕疵</li>
                <li>中古正常使用痕迹</li>
                <li>重量或尺寸轻微误差</li>
                <li>拍卖会页面已说明的问题</li>
              </ul>
              <p style={{ marginBottom: 12 }}>如商品存在重大描述错误，本平台会协助客户与拍卖会、卖家或平台沟通，但最终处理结果以对方规则为准。</p>

              <div style={{ height: 1, background: '#EAE5DC', margin: '16px 0' }} />

              <h3 style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>3. 最终说明</h3>
              <p style={{ marginBottom: 4 }}>客户提交委托后，即视为已阅读、理解并接受本协议内容。</p>
              <p style={{ marginBottom: 4 }}>云川中古会尽力协助客户确认商品信息、控制费用，并安排鉴定、改圈、运输等后续服务。</p>
              <p>本平台坚持公开透明，除明确说明的固定服务费外，不设置任何隐形消费。</p>
            </div>
          )}
        </div>

        {/* ⑥ 商品メモ / 我的备注 — localStorage 持久化 */}
        <div style={{ background: '#fff', border: `1px solid ${BD2}`, borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${BD2}`, fontSize: 12, fontWeight: 600, color: INK, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: BLUE, fontSize: 10 }}>●</span> 商品メモ / 我的备注
          </div>
          <textarea
            value={memo}
            onChange={e => setMemo(e.target.value)}
            placeholder="记录您对这件拍品的想法、出价策略或注意事项…"
            style={{
              width: '100%', padding: '12px 16px', minHeight: 80,
              border: 'none', resize: 'vertical', fontSize: 13, color: INK2,
              background: '#fff', outline: 'none', borderTop: `1px solid ${BD3}`,
              fontFamily: 'inherit',
            }}
          />
          <div style={{ padding: '8px 16px', borderTop: `1px solid ${BD2}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={saveMemo} style={{
              padding: '5px 14px', background: INK, color: '#fff',
              border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer',
              transition: 'opacity .14s',
            }}>保存</button>
            <button onClick={deleteMemo} style={{
              padding: '5px 14px', background: '#fff', color: INK3,
              border: `1px solid ${BD}`, borderRadius: 4, fontSize: 12, cursor: 'pointer',
              transition: 'all .14s',
            }}>削除</button>
          </div>
        </div>

        {/* ⑧ 我的入札记录 — 仅登录用户且有记录时展示 */}
        {isLoggedIn && (bidHistoryLoading || bidHistory.length > 0) && (
          <div style={{ background: '#fff', border: `1px solid ${BD2}`, borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${BD2}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: INK, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: BLUE, fontSize: 10 }}>●</span> 我的入札记录
              </div>
              <button
                onClick={fetchBidHistory}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: INK4, fontFamily: 'var(--mono,monospace)', padding: '2px 6px' }}
              >刷新</button>
            </div>

            {bidHistoryLoading ? (
              <div style={{ padding: '20px 0', textAlign: 'center' }}><Spin size="small" /></div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: BG2 }}>
                    {['次数', '入札金额', '入札时间', '状态'].map(h => (
                      <th key={h} style={{
                        padding: '8px 16px', fontSize: 10, fontWeight: 600, color: INK4,
                        textAlign: 'left', fontFamily: 'var(--mono,monospace)',
                        letterSpacing: '0.15em', borderBottom: `1px solid ${BD2}`,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bidHistory.map((row, i) => {
                    const success = !row.error_code && row.placed_bid_jpy;
                    const isLeading = row.is_leading === 1;
                    return (
                      <tr key={row.id} style={{ borderBottom: i < bidHistory.length - 1 ? `1px solid ${BD3}` : 'none' }}>
                        {/* 第几次入札 */}
                        <td style={{ padding: '10px 16px', fontFamily: 'var(--mono,monospace)', fontSize: 13 }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, borderRadius: '50%',
                            background: success ? (isLeading ? GREEN : BLUE) : BD,
                            color: success ? '#fff' : INK4,
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                          }}>
                            {row.seq}
                          </span>
                        </td>
                        {/* 入札金额 */}
                        <td style={{ padding: '10px 16px', fontFamily: 'var(--mono,monospace)', fontSize: 13, fontWeight: 500, color: success ? INK : INK4 }}>
                          {row.placed_bid_jpy ? formatJPY(row.placed_bid_jpy) : '—'}
                        </td>
                        {/* 入札时间 */}
                        <td style={{ padding: '10px 16px', fontFamily: 'var(--mono,monospace)', fontSize: 12, color: INK3 }}>
                          {dayjs(row.executed_at).format('MM/DD HH:mm:ss')}
                        </td>
                        {/* 状态 */}
                        <td style={{ padding: '10px 16px' }}>
                          {success ? (
                            isLeading ? (
                              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 3, background: 'rgba(42,102,69,0.1)', color: GREEN, fontWeight: 600 }}>
                                🏆 领先
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 3, background: 'rgba(26,79,140,0.1)', color: BLUE }}>
                                ✓ 已入札
                              </span>
                            )
                          ) : (
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 3, background: BG2, color: INK4 }} title={row.error_message || ''}>
                              ✗ 失败
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}


        {/* ⑦ 同场次推荐 — 5件拍品 */}
        {relatedLots.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 2, color: INK3, textTransform: 'uppercase', marginBottom: 14 }}>
              同場次の他拍品 / 同场次其他拍品
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10 }}>
              {relatedLots.map(rl => {
                let rlImgs = [];
                try { rlImgs = typeof rl.image_urls === 'string' ? JSON.parse(rl.image_urls) : (rl.image_urls || []); } catch {}
                return (
                  <div key={rl.id} onClick={() => navigate(`/lots/${rl.id}`)} style={{
                    background: '#fff', border: `1px solid ${BD2}`, borderRadius: 8,
                    padding: 10, cursor: 'pointer', transition: 'all .15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 2px 12px rgba(26,20,16,.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = BD2; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 8, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {rlImgs.length > 0 ? <img src={thumbCard(rlImgs[0])} alt="" loading="lazy" decoding="async" style={{ maxWidth: '100%', maxHeight: 50, objectFit: 'contain' }} /> : '📦'}
                    </div>
                    <div style={{ fontSize: 9, color: INK4, letterSpacing: .8, textTransform: 'uppercase', marginBottom: 2 }}>{rl.house_name || ''}</div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: INK, lineHeight: 1.4, marginBottom: 5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {rl.title_zh || rl.title_ja || ''}
                    </div>
                    <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 12, fontWeight: 500, color: INK }}>
                      {formatJPY(rl.current_price_jpy || rl.start_price_jpy)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
    {/* ── Lightbox ── */}
    {lightboxOpen && mediaItems[selectedImg]?.type !== 'video' && (
      <LightboxViewer
        images={mediaItems.filter(m => m.type !== 'video')}
        index={selectedImg}
        onClose={() => setLightboxOpen(false)}
        onChange={setSelectedImg}
        isMobile={isMobile}
      />
    )}

    {/* ══ 底部工具栏 ══ */}
      <div style={{
        position: isDialog ? 'sticky' : 'fixed', bottom: 0, left: 0, right: 0, zIndex: 90,
        background: '#fff', borderTop: `1px solid ${BD}`,
        display: 'flex', justifyContent: 'center',
        padding: isDialog ? 0 : '0 env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
      }}>
        <div style={{ display: 'flex', maxWidth: 600, width: '100%' }}>
          {[
            { icon: '📋', label: '出价规则', onClick: () => setShowBidRules(true) },
            { icon: wishlisted ? '♥' : '♡', label: wishlisted ? '已关注' : '收藏',
              color: wishlisted ? RED : undefined,
              onClick: async () => {
                if (!isLoggedIn) { navigate('/login'); return; }
                try {
                  if (wishlisted) { await api.delete(`/watchlist/${id}`); setWishlisted(false); }
                  else { await api.post('/watchlist', { lot_id: id }); setWishlisted(true); }
                } catch { message.error('操作失败'); }
              }},
            { icon: '⚖', label: '金属计价', onClick: () => setShowMetalCalc(true) },
            { icon: '🧮', label: '成本计算', onClick: () => { setCostBidInput(maxBid || String(curPrice || '')); setShowCostCalc(true); } },
          ].map(item => (
            <button key={item.label} onClick={item.onClick} style={{
              flex: 1, padding: '8px 4px 6px', background: 'transparent', border: 'none',
              cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}>
              <span style={{ fontSize: 18, color: item.color || INK3 }}>{item.icon}</span>
              <span style={{ fontSize: 10, color: item.color || INK3, whiteSpace: 'nowrap' }}>{item.label}</span>
            </button>
          ))}
        </div>
      </div>

    {/* ══ 成本计算器弹窗 ══ */}
    {showCostCalc && (() => {
      const cBid = Number(costBidInput) || 0;
      const cTax = Math.round(cBid * 0.10);
      const cSvcRate = Number(user?.service_fee_rate ?? 0.03);
      const cItemCny = Math.round(cBid * CNY_RATE);
      const cTaxCny = Math.round(cTax * CNY_RATE);
      const cSvcCny = Math.round(cBid * CNY_RATE * cSvcRate);
      const cTotalCny = cItemCny + cTaxCny + cSvcCny;
      const cFreeze = depositRequired ? Math.ceil(cBid * CNY_RATE * (Number(user?.deposit_rate ?? 0.10))) : 0;
      return (
        <div onClick={() => setShowCostCalc(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 500, maxHeight: '85vh', overflow: 'auto', padding: '20px 24px env(safe-area-inset-bottom)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: INK }}>成本计算</span>
              <button onClick={() => setShowCostCalc(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: INK4, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: INK3, marginBottom: 6 }}>输入落槌价（日元）</div>
              <input type="number" value={costBidInput} onChange={e => setCostBidInput(e.target.value)}
                placeholder="请输入预估落槌价" style={{
                  width: '100%', padding: '10px 14px', border: `1px solid ${BD}`, borderRadius: 6,
                  fontSize: 16, fontFamily: 'var(--mono,monospace)', outline: 'none', boxSizing: 'border-box',
                }} />
            </div>

            <div style={{ background: BG, borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: INK4, marginBottom: 8 }}>今日汇率：1 日元 ≈ {CNY_RATE.toFixed(4)} 人民币 {rateLocked ? '（已锁定）' : ''}</div>
              {[
                ['落槌价', `¥${cBid.toLocaleString()}`, `≈ ¥${cItemCny.toLocaleString()} CNY`],
                ['消費税（10%）', `¥${cTax.toLocaleString()}`, `≈ ¥${cTaxCny.toLocaleString()} CNY`],
                [`手续费（${(cSvcRate * 100).toFixed(1)}%）`, '', `¥${cSvcCny.toLocaleString()} CNY`],
              ].map(([label, jpy, cny]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${BD2}` }}>
                  <span style={{ fontSize: 13, color: INK2 }}>{label}</span>
                  <div style={{ textAlign: 'right' }}>
                    {jpy && <div style={{ fontSize: 12, color: INK3, fontFamily: 'var(--mono,monospace)' }}>{jpy}</div>}
                    <div style={{ fontSize: 13, color: INK, fontFamily: 'var(--mono,monospace)', fontWeight: 500 }}>{cny}</div>
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0 4px', marginTop: 4 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>合计预估</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: GOLD, fontFamily: 'var(--mono,monospace)' }}>¥{cTotalCny.toLocaleString()} CNY</span>
              </div>
            </div>

            {depositRequired > 0 && (
              <div style={{ background: 'rgba(184,137,42,0.06)', border: `1px solid rgba(184,137,42,0.2)`, borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: GOLD }}>
                预冻结保证金：¥{cFreeze.toLocaleString()} CNY（冻结比例 {((Number(user?.deposit_rate ?? 0.10)) * 100).toFixed(0)}%）
              </div>
            )}

            <div style={{ fontSize: 11, color: INK4, lineHeight: 1.6 }}>
              · 以上为预估金额，实际以结拍价为准<br />
              · 汇率取中国银行现汇卖出价，结拍时锁定<br />
              · 手续费率因会员等级而异，升级可享更低费率
            </div>
          </div>
        </div>
      );
    })()}

    {/* ══ 金属计价器弹窗 ══ */}
    {showMetalCalc && (() => {
      const METAL_PRICES = {
        gold: { label: '黄金（Au）', cnyPerG: 985.95 },
        platinum: { label: '铂金（Pt）', cnyPerG: 310.50 },
        silver: { label: '白银（Ag）', cnyPerG: 8.25 },
      };
      const mt = METAL_PRICES[metalType] || METAL_PRICES.gold;
      const w = Number(metalWeight) || 0;
      const p = Number(metalPurity) || 0;
      const disc = Number(metalDiscount) || 0;
      const metalValue = mt.cnyPerG * (1 - disc / 100) * w * p;
      const metalValueJpy = CNY_RATE > 0 ? metalValue / CNY_RATE : 0;
      return (
        <div onClick={() => setShowMetalCalc(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 500, maxHeight: '85vh', overflow: 'auto', padding: '20px 24px env(safe-area-inset-bottom)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: INK }}>金属计价参考</span>
              <button onClick={() => setShowMetalCalc(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: INK4, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ background: 'rgba(26,79,140,0.05)', border: '1px solid rgba(26,79,140,0.15)', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: BLUE }}>
              参考价 = 单价 × (1 − 折损率) × 重量 × 纯度
            </div>

            {[
              { label: '金属类型', content: (
                <div style={{ display: 'flex', gap: 6 }}>
                  {Object.entries(METAL_PRICES).map(([k, v]) => (
                    <button key={k} onClick={() => setMetalType(k)} style={{
                      padding: '6px 12px', borderRadius: 16, fontSize: 12, cursor: 'pointer',
                      border: `1px solid ${metalType === k ? GOLD : BD}`,
                      background: metalType === k ? 'rgba(184,137,42,0.08)' : '#fff',
                      color: metalType === k ? GOLD : INK3,
                    }}>{v.label}</button>
                  ))}
                </div>
              )},
              { label: '今日单价（元/克）', value: mt.cnyPerG.toFixed(2) },
              { label: '折损率（%）', input: true, val: metalDiscount, set: setMetalDiscount, placeholder: '如：1' },
              { label: '重量（g）', input: true, val: metalWeight, set: setMetalWeight, placeholder: '请输入重量' },
              { label: '纯度（0~1）', input: true, val: metalPurity, set: setMetalPurity, placeholder: '如：0.999' },
              { label: '今日汇率', value: `1 日元 ≈ ${CNY_RATE.toFixed(4)} 人民币` },
            ].map((row, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${BD2}` }}>
                <span style={{ fontSize: 13, color: INK2, flexShrink: 0 }}>{row.label}</span>
                {row.content || (row.input ? (
                  <input type="number" value={row.val} onChange={e => row.set(e.target.value)} placeholder={row.placeholder}
                    style={{ width: 120, padding: '6px 10px', border: `1px solid ${BD}`, borderRadius: 4, fontSize: 14, textAlign: 'right', fontFamily: 'var(--mono,monospace)', outline: 'none' }} />
                ) : (
                  <span style={{ fontSize: 14, color: INK, fontFamily: 'var(--mono,monospace)', fontWeight: 500 }}>{row.value}</span>
                ))}
              </div>
            ))}

            <div style={{ background: BG, borderRadius: 8, padding: 16, marginTop: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: INK4, marginBottom: 6 }}>贵金属参考价</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: GOLD, fontFamily: 'var(--mono,monospace)' }}>
                ¥{metalValue > 0 ? metalValue.toFixed(0) : '—'} <span style={{ fontSize: 13, fontWeight: 400, color: INK3 }}>CNY</span>
              </div>
              {metalValueJpy > 0 && (
                <div style={{ fontSize: 13, color: INK4, fontFamily: 'var(--mono,monospace)', marginTop: 4 }}>
                  ≈ ¥{Math.round(metalValueJpy).toLocaleString()} JPY
                </div>
              )}
            </div>

            <div style={{ fontSize: 11, color: INK4, lineHeight: 1.6, marginTop: 12 }}>
              · 金属价格取上海黄金交易所参考价，仅供估价参考<br />
              · 实际价值需以专业鉴定为准
            </div>
          </div>
        </div>
      );
    })()}

    {/* ══ 出价规则弹窗 ══ */}
    {showBidRules && (
      <div onClick={() => setShowBidRules(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 500, maxHeight: '85vh', overflow: 'auto', padding: '20px 24px env(safe-area-inset-bottom)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: INK }}>出价规则</span>
            <button onClick={() => setShowBidRules(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: INK4, cursor: 'pointer' }}>✕</button>
          </div>

          {/* 一、拍行手续费 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>一、拍行手续费</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: BG }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', color: INK3, fontWeight: 500, borderBottom: `1px solid ${BD2}` }}>品类</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: INK3, fontWeight: 500, borderBottom: `1px solid ${BD2}` }}>手续费率</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['箱包 / 珠宝 / 手表', '3%'],
                  ['鞋服 ＜¥10,000', '15%'],
                  ['鞋服 ¥10,001〜¥20,000', '10%'],
                  ['鞋服 ＞¥20,001', '5%'],
                ].map(([cat, rate]) => (
                  <tr key={cat}>
                    <td style={{ padding: '8px 10px', borderBottom: `1px solid ${BD2}`, color: INK2 }}>{cat}</td>
                    <td style={{ padding: '8px 10px', borderBottom: `1px solid ${BD2}`, color: GOLD, fontWeight: 600, textAlign: 'right', fontFamily: 'var(--mono,monospace)' }}>{rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 11, color: INK4, marginTop: 6 }}>
              · 会员等级越高，手续费率越低<br />
              · 您当前手续费率：<span style={{ color: GOLD, fontWeight: 600 }}>{((Number(user?.service_fee_rate ?? 0.03)) * 100).toFixed(1)}%</span>
            </div>
          </div>

          {/* 二、出价规则 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>二、加价阶梯</div>
            <div style={{ fontSize: 12, color: INK3, marginBottom: 8 }}>最终成交价格为第二高出价者的出价金额，加上以下追加金额：</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: BG }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', color: INK3, fontWeight: 500, borderBottom: `1px solid ${BD2}` }}>出价区间</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: INK3, fontWeight: 500, borderBottom: `1px solid ${BD2}` }}>加价幅度</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['5万日元未满', '¥1,000'],
                  ['5万〜10万日元', '¥2,000'],
                  ['10万〜30万日元', '¥5,000'],
                  ['30万〜50万日元', '¥10,000'],
                  ['50万〜100万日元', '¥20,000'],
                  ['100万日元以上', '¥30,000'],
                ].map(([range, step]) => (
                  <tr key={range}>
                    <td style={{ padding: '8px 10px', borderBottom: `1px solid ${BD2}`, color: INK2 }}>{range}</td>
                    <td style={{ padding: '8px 10px', borderBottom: `1px solid ${BD2}`, color: INK, fontWeight: 500, textAlign: 'right', fontFamily: 'var(--mono,monospace)' }}>{step}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ background: 'rgba(155,35,53,0.06)', border: '1px solid rgba(155,35,53,0.15)', borderRadius: 6, padding: '8px 12px', marginTop: 8, fontSize: 12, color: RED }}>
              ★ 仅限手表：30万日元以上一律加价 ¥10,000
            </div>
          </div>

          {/* 三、退货规则 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>三、退货规则</div>
            <div style={{ fontSize: 13, color: INK2, lineHeight: 1.8 }}>
              <div style={{ marginBottom: 6 }}><strong>【时间规则】</strong></div>
              · 未送达、商品缺失或包装损坏：14天<br />
              · 商品被认定为赝品/盗品：无限制<br />
              · 商品被认定为非正品或不合规：1天<br />
              · 信息不符或瑕疵：珠宝1个月 / 手表·箱包·服饰14天<br /><br />
              <div style={{ marginBottom: 6 }}><strong>【不可退货的情况】</strong></div>
              · 商品信息中标记"现状"的商品<br />
              · 拍卖后商品状态发生变化<br />
              · 等级定义范围内的瑕疵
            </div>
          </div>
        </div>
      </div>
    )}

    </>
  );
}

export default function LotDetail() {
  return <LotDetailContent />;
}
